/**
 * ImageOverlayPanel — manual brand-template overlay controls for the v2
 * card hero image. Lets the user type the headline + subhead, pick fonts,
 * sizes, colours, anchor (tl/tr/bl/br) and nudge offsets, and toggle the
 * logo + contact pill. All changes are debounced by 3s and persisted via
 * POST /api/v2/workspaces/{ws}/cards/{id}/image/compose, which re-renders
 * the PNG and saves the spec back to card.image_overlay.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { CachedImage } from "./CachedImage";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Loader2, Type, Move, Upload, Image as ImgIcon, EyeOff, Eye, Layers, Shield, Sparkles, Crop,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, FlipHorizontal2, FlipVertical2, RotateCw, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const DEFAULT_OVERLAY = {
  headline: "",
  subhead: "",
  anchor: "tl",
  offset_x: 0,
  offset_y: 0,
  flip_horizontal: false,
  flip_vertical: false,
  rotate: 0,
  line_spacing: null,
  heading_font: "brand:heading",
  heading_size: null,
  heading_color: null,
  heading_weight: "bold",
  heading_italic: false,
  heading_transform: "none",
  heading_letter_spacing: 0,
  accent_font: "brand:accent",
  accent_size: null,
  accent_color: null,
  accent_weight: "regular",
  accent_italic: false,
  accent_transform: "none",
  accent_letter_spacing: 0,
  show_logo: true,
  show_contact_pill: true,
  custom_font_path: null,
  preset: "bold",
  overlay_enabled: true,
  overlay_color: "#0a22e0",
  overlay_opacity: 0.55,
  overlay_style: "left",
  duotone_enabled: false,
  duotone_shadow: "#0a2a43",
  duotone_highlight: "#dff1fb",
  duotone_strength: 1.0,
  text_shadow: false,
  text_shadow_opacity: 0.55,
  text_shadow_blur: 4,
  text_shadow_distance: 4,
  text_shadow_direction: 135,
  text_stroke: false,
  text_stroke_width: 2,
  text_stroke_color: "#000000",
  text_box_enabled: false,
  text_box_style: "semi",
  text_box_color: "#ffffff",
  text_box_opacity: 0.85,
  text_box_radius: 16,
  text_box_padding: 24,
  text_box_blur: 12,
  text_box_shadow: false,
  logo_variant: "primary",
  logo_auto_contrast: false,
  logo_position: "tr",
  logo_size: 100,
  logo_opacity: 1.0,
  logo_margin: 60,
  logo_offset_x: 0,
  logo_offset_y: 0,
  logo_shadow: false,
  // Shared logo drop-shadow params (apply to brand + client logos)
  logo_shadow_opacity: 0.45,
  logo_shadow_blur: 18,
  logo_shadow_distance: 8,
  logo_shadow_direction: 135,
  // Second (client) logo layer
  client_logo_path: null,
  client_logo_position: "bl",
  client_logo_size: 100,
  client_logo_opacity: 1.0,
  client_logo_margin: 60,
  client_logo_offset_x: 0,
  client_logo_offset_y: 0,
  client_logo_shadow: false,
  // Movable subject layer (Compose Studio → Director hand-off)
  subject_path: null,
  subject_show: true,
  subject_x: 0.5,
  subject_y: 0.5,
  subject_scale: 1.0,
  subject_box_w: 0.6,
  subject_box_h: 0.6,
  subject_opacity: 1.0,
  // Multi-object subject layers (up to 5). Each: {path, show, x, y, scale,
  // box_w, box_h, opacity}. Canonical for multi-subject cards.
  subjects: [],
  show_tagline: true,
  tagline_color: null,
  tagline_size: 22,
  tagline_text: null,
  brand_name_text: null,
  contact_pill_position: "bl",
  contact_pill_size: 100,
  contact_pill_offset_x: 0,
  contact_pill_offset_y: 0,
  contact_text: null,
  format: "square_1080",
  // Independent sub-header positioning (covers)
  subhead_offset_x: 0,
  subhead_offset_y: 0,
  subhead_independent: false,
  // Cover meta block (date / prepared-by / free note)
  show_cover_meta: false,
  meta_date: "",
  meta_prepared_by: "",
  meta_custom: "",
  meta_anchor: "br",
  meta_offset_x: 0,
  meta_offset_y: 0,
  meta_size: 0,
  // Non-destructive crop (fractions of canvas; w/h=0 → no crop)
  crop_x: 0,
  crop_y: 0,
  crop_w: 0,
  crop_h: 0,
};

const ARTIFACT_FORMATS = [
  { key: "square_1080",     label: "Square (1080×1080)",      ratio: "aspect-square" },
  { key: "cover_landscape", label: "Cover page (1920×1080)",  ratio: "aspect-video" },
  { key: "a4_portrait",     label: "A4 portrait one-pager",   ratio: "aspect-[2480/3508]" },
  { key: "linkedin_banner", label: "LinkedIn banner",          ratio: "aspect-[1584/396]" },
  { key: "email_header",    label: "Email header",             ratio: "aspect-[3/1]" },
  { key: "story_9_16",      label: "Story / Reel (9:16)",      ratio: "aspect-[9/16]" },
  { key: "carousel_4_5",    label: "Carousel slide (4:5)",     ratio: "aspect-[4/5]" },
];

// Per-format default text sizes (mirror backend ARTIFACT_FORMATS heading/accent
// sizes) so the size inputs show the true effective size + a sensible range for
// big canvases (A4 covers need ~200/90, not the social 96/46).
const FORMAT_SIZE_DEFAULTS = {
  square_1080:     { heading: 96,  accent: 46 },
  cover_landscape: { heading: 130, accent: 56 },
  a4_portrait:     { heading: 200, accent: 90 },
  linkedin_banner: { heading: 80,  accent: 36 },
  email_header:    { heading: 70,  accent: 32 },
  story_9_16:      { heading: 110, accent: 50 },
  carousel_4_5:    { heading: 100, accent: 48 },
};

const ANCHOR_LABELS = {
  tl: "Top-left",    tc: "Top-center",    tr: "Top-right",
  ml: "Middle-left", mc: "Middle-center", mr: "Middle-right",
  bl: "Bottom-left", bc: "Bottom-center", br: "Bottom-right",
};
const ANCHOR_GRID_ORDER = ["tl", "tc", "tr", "ml", "mc", "mr", "bl", "bc", "br"];

const WEIGHT_OPTIONS = [
  { key: "regular", label: "Regular" },
  { key: "bold",    label: "Bold" },
];
const TRANSFORM_OPTIONS = [
  { key: "none",  label: "Aa" },
  { key: "upper", label: "AA" },
  { key: "lower", label: "aa" },
  { key: "title", label: "Aa Aa" },
];
const PREVIEW_FRAMES = [
  { key: "full",      label: "Full 1:1",     wrapClass: "w-full aspect-square" },
  { key: "linkedin",  label: "LinkedIn feed", wrapClass: "w-full max-w-[400px] mx-auto aspect-square ring-2 ring-[#0077b5]/60 rounded-xl overflow-hidden" },
  { key: "instagram", label: "Instagram",    wrapClass: "w-[85%] max-w-[340px] mx-auto aspect-square ring-2 ring-pink-500/60 rounded-xl overflow-hidden" },
  { key: "mobile",    label: "Mobile thumb", wrapClass: "w-[60%] max-w-[220px] mx-auto aspect-square ring-2 ring-slate-400 rounded-md overflow-hidden" },
];
const STRESS_FILTERS = {
  none:      "",
  blur:      "filter blur-[3px]",
  greyscale: "filter grayscale contrast-125",
};

// ── Live (instant) preview helpers ───────────────────────────────────────
// Approximate the server compositor in the browser so edits show instantly.
// The server PNG remains the source of truth and swaps in once it's ready.
const FONT_STACK_HEADING = '"Arial Black", "Helvetica Neue", Helvetica, Arial, sans-serif';
const FONT_STACK_ACCENT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const CSS_TRANSFORM = { none: "none", upper: "uppercase", lower: "lowercase", title: "capitalize" };
const hexToRgba = (hex, a) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return `rgba(10,34,224,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
};

const LOGO_VARIANT_OPTIONS = [
  { key: "primary", label: "Primary (blue)" },
  { key: "white", label: "White" },
  { key: "dark", label: "Dark" },
  { key: "icon", label: "Icon only" },
  { key: "none", label: "No logo" },
];

const OVERLAY_STYLE_OPTIONS = [
  { key: "none", label: "None" },
  { key: "full", label: "Full image" },
  { key: "left", label: "Left gradient" },
  { key: "right", label: "Right gradient" },
  { key: "top", label: "Top gradient" },
  { key: "bottom", label: "Bottom gradient" },
  { key: "radial", label: "Radial focus" },
];

const TEXT_BOX_STYLE_OPTIONS = [
  { key: "solid", label: "Solid" },
  { key: "semi", label: "Semi-transparent" },
  { key: "glass", label: "Glass blur" },
  { key: "soft", label: "Soft brand panel" },
  { key: "outline", label: "Outline only (no fill)" },
];

// RESILIGENT brand blue — used by the one-click brand-wash preset.
const RESILIGENT_BRAND_BLUE = "#0d7cb4";

const PRESET_OPTIONS = [
  { key: "bold", label: "Bold headline" },
  { key: "editorial", label: "Editorial" },
  { key: "minimal", label: "Minimal" },
  { key: "campaign", label: "Campaign" },
  { key: "announcement", label: "Announcement" },
];

const BRAND_SWATCHES = ["#0d7cb4", "#00a6da", "#0a3d56", "#1f2937", "#ffffff"];

const HANDLE_LABELS = {
  text: "Title", logo: "Logo", pill: "Pill",
  subhead: "Sub-header", clientlogo: "Client logo", covermeta: "Info block", subject: "Subject",
};

// Cover-only defaults — applied in BOTH the state initializer and the hydrate
// effect so reopening a cover never loses them (DRY; see iter317 bug).
function applyCoverDefaults(merged, card) {
  if (card?.card_type !== "COVER_DESIGN") return merged;
  merged.show_contact_pill = false; // covers use a footer / client logo, not the pill
  if (merged.show_cover_meta === undefined) merged.show_cover_meta = true;
  if (!merged.meta_prepared_by) merged.meta_prepared_by = "Prepared by RESILIGENT";
  if (!merged.meta_date) merged.meta_date = new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return merged;
}

export default function ImageOverlayPanel({ workspaceId, cardId, card, onComposed }) {
  const isCover = card?.card_type === "COVER_DESIGN";
  const [zoom, setZoom] = useState(1);
  const [cropping, setCropping] = useState(false);
  const [cropRect, setCropRect] = useState({ x: 0.06, y: 0.06, w: 0.88, h: 0.88 });
  const [cropDrag, setCropDrag] = useState(null);
  const [overlay, setOverlay] = useState(() =>
    applyCoverDefaults({ ...DEFAULT_OVERLAY, ...(card?.image_overlay || {}) }, card)
  );
  const [fonts, setFonts] = useState([]);
  const [composing, setComposing] = useState(false);
  const [imgVersion, setImgVersion] = useState(0); // cache-bust
  const [previewMode, setPreviewMode] = useState("composed"); // "composed" | "plate"
  const [readability, setReadability] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [placement, setPlacement] = useState(null);
  const [previewFrame, setPreviewFrame] = useState("full");
  const [stressFilter, setStressFilter] = useState("none");
  const [drag, setDrag] = useState(null); // {startX, startY, startOffsetX, startOffsetY}
  const [activeHandle, setActiveHandle] = useState("text"); // text | logo | pill (for keyboard nudge)
  const [activeSubjectIdx, setActiveSubjectIdx] = useState(0);
  const previewRef = useRef(null);
  const [availableLogoVariants, setAvailableLogoVariants] = useState([]);
  const dirtyRef = useRef(false);
  const debounceRef = useRef(null);
  const lastSentRef = useRef(JSON.stringify(overlay));
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay; // always-latest overlay for event handlers / async closures
  const fileInputRef = useRef(null);
  // Measure rendered preview width so the instant live-text layer can scale
  // font sizes from canvas px → screen px while editing.
  const [stageW, setStageW] = useState(0);
  useEffect(() => {
    const el = previewRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      setStageW(entries[0]?.contentRect?.width || el.clientWidth || 0);
    });
    ro.observe(el);
    setStageW(el.clientWidth || 0);
    return () => ro.disconnect();
  }, [card?.image_url, card?.image_plate_path]);

  // Force a cache-bust reload whenever the hero image URL changes from the
  // parent (upload / stock attach / abstract pick / clear). Without this, an
  // upload that reuses the same object path would keep showing the cached
  // (Rex-generated) image even though the file behind the URL changed.
  useEffect(() => {
    setImgVersion((v) => v + 1);
  }, [card?.image_url]);
  const historyRef = useRef({ stack: [JSON.stringify(overlay)], idx: 0 });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const refreshUndoFlags = useCallback(() => {
    const h = historyRef.current;
    setCanUndo(h.idx > 0);
    setCanRedo(h.idx < h.stack.length - 1);
  }, []);

  const pushHistory = useCallback((spec) => {
    const h = historyRef.current;
    const snap = JSON.stringify(spec);
    if (h.stack[h.idx] === snap) return;        // no-op change
    h.stack = h.stack.slice(0, h.idx + 1);      // drop any redo branch
    h.stack.push(snap);
    h.idx = h.stack.length - 1;
    if (h.stack.length > 60) { h.stack.shift(); h.idx -= 1; }
    refreshUndoFlags();
  }, [refreshUndoFlags]);

  // Hydrate from card when it changes (drawer opening / new card).
  // IMPORTANT: only (re)initialise the undo history when we switch to a
  // DIFFERENT card — NOT on every recompose (each successful compose updates
  // card.image_overlay, which would otherwise wipe the history and keep Undo
  // permanently disabled).
  const hydratedCardRef = useRef(null);
  useEffect(() => {
    const merged = applyCoverDefaults({ ...DEFAULT_OVERLAY, ...(card?.image_overlay || {}) }, card);
    // Migrate a legacy single subject_* layer into the canonical subjects[]
    // array so the multi-object UI can drive it. Idempotent.
    if ((!Array.isArray(merged.subjects) || merged.subjects.length === 0) && merged.subject_path) {
      merged.subjects = [{
        path: merged.subject_path,
        show: merged.subject_show !== false,
        x: merged.subject_x ?? 0.5,
        y: merged.subject_y ?? 0.5,
        scale: merged.subject_scale ?? 1.0,
        box_w: merged.subject_box_w ?? 0.6,
        box_h: merged.subject_box_h ?? 0.6,
        opacity: merged.subject_opacity ?? 1.0,
      }];
      merged.subject_path = null; // array supersedes the legacy single layer
    }
    if (!Array.isArray(merged.subjects)) merged.subjects = [];
    // If card has no overlay yet and we have a title, seed headline so the
    // user has *something* to start from instead of an empty post.
    if (!card?.image_overlay && card?.title && !merged.headline) {
      merged.headline = (card.title || "").slice(0, 100);
    }
    setOverlay(merged);
    lastSentRef.current = JSON.stringify(merged);
    dirtyRef.current = false;
    if (hydratedCardRef.current !== cardId) {
      hydratedCardRef.current = cardId;
      historyRef.current = { stack: [JSON.stringify(merged)], idx: 0 };
      setCanUndo(false);
      setCanRedo(false);
    } else {
      // Same card, overlay changed (e.g. our own recompose) — keep history.
      const h = historyRef.current;
      setCanUndo(h.idx > 0);
      setCanRedo(h.idx < h.stack.length - 1);
    }
  }, [cardId, card?.image_overlay, card?.title]);

  // Load fonts list
  useEffect(() => {
    if (!workspaceId || !cardId) return;
    let cancelled = false;
    fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/fonts`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : { fonts: [] }))
      .then(d => { if (!cancelled) setFonts(d.fonts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [workspaceId, cardId]);

  const compose = useCallback(async (spec, fromHistory = false) => {
    if (!workspaceId || !cardId) return;
    setComposing(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/compose`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(spec),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Recompose failed (${r.status})`);
      lastSentRef.current = JSON.stringify(spec);
      dirtyRef.current = false;
      setImgVersion(v => v + 1);
      // Record an undo checkpoint for every rendered state — unless this compose
      // was itself an undo/redo replay. The flag is passed PER CALL (not a shared
      // ref) so overlapping composes can never clear each other's intent.
      if (!fromHistory) pushHistory(spec);
      refreshUndoFlags();
      // Surface missing-logo-variant warning so the user can fix it instead of
      // silently rendering the primary blue logo.
      if (body.logo_variant_warning) {
        toast.warning(body.logo_variant_warning, { duration: 8000 });
      }
      // Track which variants are actually uploaded so we can render
      // "Not uploaded" badges in the variant dropdown.
      if (Array.isArray(body.available_logo_variants)) {
        setAvailableLogoVariants(body.available_logo_variants);
      }
      onComposed && onComposed(body);
      // Refresh readability after each successful compose
      checkReadability();
    } catch (e) {
      toast.error(e.message || "Recompose failed");
    } finally {
      setComposing(false);
    }
  }, [workspaceId, cardId, onComposed, pushHistory, refreshUndoFlags]);

  // Cover variation switcher — re-bridge the selected background variant onto
  // this card (keeps the current text/logo overlay, just swaps the plate).
  const switchVariant = useCallback(async (side, variantIndex) => {
    if (!workspaceId || !cardId) return;
    const runId = overlay.cover_run_id;
    if (!runId) return;
    setComposing(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/from-cover`,
        {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            run_id: runId, side, variant_index: variantIndex,
            format: overlay.format || "a4_portrait",
            cover_variants: overlay.cover_variants || [],
            cover_orientation: overlay.cover_orientation || null,
          }),
        }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Switch failed (${r.status})`);
      if (body.overlay) setOverlay((prev) => ({ ...prev, ...body.overlay }));
      setImgVersion((v) => v + 1);
      onComposed && onComposed(body);
      checkReadability();
      toast.success(`Switched to ${side} variation ${variantIndex + 1}`);
    } catch (e) {
      toast.error(e.message || "Couldn't switch variation");
    } finally {
      setComposing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, cardId, overlay.cover_run_id, overlay.cover_variants, overlay.format, onComposed]);

  const checkReadability = useCallback(async () => {
    if (!workspaceId || !cardId) return;
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/readability`,
        { credentials: "include" }
      );
      if (r.ok) {
        const body = await r.json();
        setReadability(body);
      }
    } catch {
      // best-effort; don't bother the user if the check fails
    }
  }, [workspaceId, cardId]);

  // Initial readability check when the card loads / changes
  useEffect(() => {
    if (card?.image_plate_path) checkReadability();
  }, [cardId, card?.image_plate_path, checkReadability]);

  // Load placement suggestions (quiet zones) once per card
  useEffect(() => {
    if (!workspaceId || !cardId || !card?.image_plate_path) return;
    let cancelled = false;
    fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/placement-suggestions`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d) setPlacement(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [workspaceId, cardId, card?.image_plate_path]);

  const fixReadability = async () => {
    if (fixing) return;
    setFixing(true);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/fix-readability`,
        { method: "POST", credentials: "include" }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || "Fix failed");
      if (body.applied) {
        toast.success(body.rationale || "Readability improved");
        setOverlay((prev) => {
          const next = { ...prev, ...(body.overlay || {}) };
          pushHistory(next);
          return next;
        });
        setImgVersion(v => v + 1);
        setReadability(null);
        // Refresh
        setTimeout(checkReadability, 500);
        onComposed && onComposed(body);
      } else {
        toast.info("Already strong — no fix needed");
      }
    } catch (e) {
      toast.error(e.message || "Fix failed");
    } finally {
      setFixing(false);
    }
  };

  // Debounced auto-compose: 3s after last change
  useEffect(() => {
    if (!dirtyRef.current) return;
    if (JSON.stringify(overlay) === lastSentRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      compose(overlay);
    }, 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [overlay, compose]);

  const update = (patch) => {
    dirtyRef.current = true;
    setOverlay((prev) => ({ ...prev, ...patch }));
  };

  // Update a single subject layer in the subjects[] array (multi-object).
  // recompose=true commits immediately; otherwise the debounced auto-compose
  // picks it up (used during live drag where we compose on release).
  const updateSubject = (idx, patch, recompose = false) => {
    const cur = overlayRef.current.subjects || [];
    const nextSubjects = cur.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    if (recompose) {
      updateCompose({ subjects: nextSubjects });
    } else {
      update({ subjects: nextSubjects });
    }
  };

  const removeSubject = (idx) => {
    const cur = overlayRef.current.subjects || [];
    const nextSubjects = cur.filter((_, i) => i !== idx);
    setActiveSubjectIdx((p) => Math.max(0, Math.min(p, nextSubjects.length - 1)));
    updateCompose({ subjects: nextSubjects });
  };

  // Z-order: swap the active subject with its neighbour. Later in the array =
  // rendered on top (forward). dir=+1 brings forward, dir=-1 sends back.
  const moveSubject = (idx, dir) => {
    const cur = [...(overlayRef.current.subjects || [])];
    const j = idx + dir;
    if (j < 0 || j >= cur.length) return;
    [cur[idx], cur[j]] = [cur[j], cur[idx]];
    setActiveSubjectIdx(j);
    updateCompose({ subjects: cur });
  };

  // Patch the overlay AND recompose immediately (no debounce) — used for
  // discrete cover actions (toggle, variant pick) where waiting feels broken.
  const updateCompose = (patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next = { ...overlayRef.current, ...patch };
    // Fire EXACTLY ONE compose. Calling compose() here (outside the setState
    // updater) keeps it pure so React StrictMode's double-invoke can't spawn a
    // duplicate render, and marking the spec as already-sent stops the
    // debounced auto-compose effect from firing a second racing render.
    dirtyRef.current = false;
    lastSentRef.current = JSON.stringify(next);
    setOverlay(next);
    compose(next);
  };

  // Drag-drop position on the preview. Pixel delta on the rendered image maps
  // to the active element's compositor offset (scaled to the 1080px canvas).
  // target ∈ {"text","logo","pill"} → drives which offset pair we move.
  const OFFSET_KEYS = {
    text: ["offset_x", "offset_y"],
    logo: ["logo_offset_x", "logo_offset_y"],
    pill: ["contact_pill_offset_x", "contact_pill_offset_y"],
    subhead: ["subhead_offset_x", "subhead_offset_y"],
    clientlogo: ["client_logo_offset_x", "client_logo_offset_y"],
    covermeta: ["meta_offset_x", "meta_offset_y"],
  };
  // Canvas pixel dimensions per format — so drag deltas & handle positions are
  // accurate on tall A4 covers (not just 1080² squares).
  const FORMAT_DIMS = {
    square_1080: [1080, 1080], cover_landscape: [1920, 1080],
    a4_portrait: [2480, 3508], a4_landscape: [3508, 2480],
    linkedin_banner: [1584, 396], email_header: [1200, 400],
    story_9_16: [1080, 1920], carousel_4_5: [1080, 1350],
  };
  const [cw, ch] = FORMAT_DIMS[overlay.format] || [1080, 1080];
  const onDragStart = (e, target = "text") => {
    e.preventDefault();
    if (target !== "text") e.stopPropagation();
    setActiveHandle(target);
    previewRef.current?.focus?.();
    const pt = e.touches ? e.touches[0] : e;
    const [kx, ky] = OFFSET_KEYS[target];
    // Dragging the sub-header detaches it from the headline.
    if (target === "subhead" && !overlay.subhead_independent) {
      setOverlay((prev) => ({ ...prev, subhead_independent: true }));
      dirtyRef.current = true;
    }
    // Base (offset-free) handle position in canvas px — used to soft-clamp the
    // drag so the element's anchor can't slide off the canvas.
    const hp = handlePos(target);
    const baseHandleX = (parseFloat(hp.left) / 100) * cw - (overlay[kx] || 0);
    const baseHandleY = (parseFloat(hp.top) / 100) * ch - (overlay[ky] || 0);
    setDrag({ startX: pt.clientX, startY: pt.clientY, baseX: overlay[kx] || 0, baseY: overlay[ky] || 0, baseHandleX, baseHandleY, target });
  };
  const onDragMove = (e) => {
    if (!drag || !previewRef.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const rect = previewRef.current.getBoundingClientRect();
    const scaleX = cw / rect.width;
    const scaleY = ch / rect.height;
    const dx = (pt.clientX - drag.startX) * scaleX;
    const dy = (pt.clientY - drag.startY) * scaleY;
    // Soft clamp: keep the element's anchor point inside the canvas (with a
    // small padding) instead of the old symmetric ±half-canvas limit, so text
    // can no longer be dragged off the visible plate.
    const pad = Math.round(Math.min(cw, ch) * 0.02);
    const bx = drag.baseHandleX ?? 0;
    const by = drag.baseHandleY ?? 0;
    const nx = Math.round(Math.max(pad - bx, Math.min(cw - pad - bx, drag.baseX + dx)));
    const ny = Math.round(Math.max(pad - by, Math.min(ch - pad - by, drag.baseY + dy)));
    const [kx, ky] = OFFSET_KEYS[drag.target || "text"];
    if (nx !== overlay[kx] || ny !== overlay[ky]) {
      dirtyRef.current = true;
      setOverlay((prev) => ({ ...prev, [kx]: nx, [ky]: ny }));
    }
  };
  const onDragEnd = () => {
    if (drag) {
      const [kx, ky] = OFFSET_KEYS[drag.target || "text"];
      const o = overlayRef.current;
      const moved = (o[kx] || 0) !== drag.baseX || (o[ky] || 0) !== drag.baseY;
      if (dirtyRef.current || moved) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        compose(o);
      }
    }
    setDrag(null);
  };

  // Subject layer drag/resize on the preview — normalised canvas-fraction
  // centre (subject_x/subject_y) + a scale multiplier. Self-contained window
  // listeners (the subject uses fractions, not the pixel-offset OFFSET_KEYS).
  const onSubjectDragStart = (e, idx = activeSubjectIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveHandle("subject");
    setActiveSubjectIdx(idx);
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const onMove = (ev) => {
      const pt = ev.touches ? ev.touches[0] : ev;
      const px = (pt.clientX - rect.left) / rect.width;
      const py = (pt.clientY - rect.top) / rect.height;
      dirtyRef.current = true;
      setOverlay((prev) => ({
        ...prev,
        subjects: (prev.subjects || []).map((s, i) =>
          i === idx ? { ...s, x: Math.max(0.02, Math.min(0.98, px)), y: Math.max(0.02, Math.min(0.98, py)) } : s
        ),
      }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      compose(overlayRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };
  const onSubjectResizeStart = (e, idx = activeSubjectIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveHandle("subject");
    setActiveSubjectIdx(idx);
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = (overlayRef.current.subjects || [])[idx] || {};
    const centerX = rect.left + (s.x ?? 0.5) * rect.width;
    const centerY = rect.top + (s.y ?? 0.5) * rect.height;
    const baseHalf = ((s.box_w ?? 0.6) * rect.width) / 2;
    const onMove = (ev) => {
      const pt = ev.touches ? ev.touches[0] : ev;
      const half = Math.max(Math.abs(pt.clientX - centerX), Math.abs(pt.clientY - centerY));
      const next = Math.max(0.2, Math.min(4.0, half / Math.max(1, baseHalf)));
      dirtyRef.current = true;
      setOverlay((prev) => ({
        ...prev,
        subjects: (prev.subjects || []).map((sub, i) =>
          i === idx ? { ...sub, scale: Math.round(next * 100) / 100 } : sub
        ),
      }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      compose(overlayRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  // Robust drag: track move/up on the WINDOW while a drag is active. This
  // fixes handles that sit at the canvas edge (e.g. the bottom-left client
  // logo) where the cursor leaves the preview stage — previously the stage's
  // onMouseLeave fired onDragEnd prematurely and the release never recomposed.
  // A release anywhere now commits + recomposes exactly once.
  useEffect(() => {
    if (!drag) return;
    const move = (e) => onDragMove(e);
    const end = () => onDragEnd();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  // ── Non-destructive crop ──
  const startCrop = () => {
    const o = overlay;
    if (o.crop_w && o.crop_h) setCropRect({ x: o.crop_x || 0, y: o.crop_y || 0, w: o.crop_w, h: o.crop_h });
    else setCropRect({ x: 0.06, y: 0.06, w: 0.88, h: 0.88 });
    setCropping(true);
  };
  const onCropDown = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    const pt = e.touches ? e.touches[0] : e;
    setCropDrag({ startX: pt.clientX, startY: pt.clientY, base: { ...cropRect }, mode });
  };
  const onCropMove = (e) => {
    if (!cropDrag || !previewRef.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const rect = previewRef.current.getBoundingClientRect();
    const dx = (pt.clientX - cropDrag.startX) / rect.width;
    const dy = (pt.clientY - cropDrag.startY) / rect.height;
    const b = cropDrag.base;
    let { x, y, w, h } = b;
    const c01 = (v) => Math.max(0, Math.min(1, v));
    if (cropDrag.mode === "move") {
      x = Math.min(c01(b.x + dx), 1 - b.w);
      y = Math.min(c01(b.y + dy), 1 - b.h);
    } else if (cropDrag.mode === "se") {
      w = Math.max(0.1, Math.min(1 - b.x, b.w + dx));
      h = Math.max(0.1, Math.min(1 - b.y, b.h + dy));
    } else if (cropDrag.mode === "nw") {
      const nx = c01(b.x + dx), ny = c01(b.y + dy);
      x = Math.min(nx, b.x + b.w - 0.1); y = Math.min(ny, b.y + b.h - 0.1);
      w = Math.max(0.1, b.w + (b.x - x)); h = Math.max(0.1, b.h + (b.y - y));
    }
    setCropRect({ x, y, w, h });
  };
  const onCropEnd = () => setCropDrag(null);
  const applyCrop = () => {
    setCropping(false);
    updateCompose({
      crop_x: +cropRect.x.toFixed(4), crop_y: +cropRect.y.toFixed(4),
      crop_w: +cropRect.w.toFixed(4), crop_h: +cropRect.h.toFixed(4),
    });
  };
  const resetCrop = () => {
    setCropping(false);
    updateCompose({ crop_x: 0, crop_y: 0, crop_w: 0, crop_h: 0 });
  };


  // Keyboard arrow-key nudging of the ACTIVE element (10px; Shift = 1px fine).
  const onKeyNudge = (e) => {
    let dx = 0, dy = 0;
    const step = e.shiftKey ? 1 : 10;
    if (e.key === "ArrowLeft") dx = -step;
    else if (e.key === "ArrowRight") dx = step;
    else if (e.key === "ArrowUp") dy = -step;
    else if (e.key === "ArrowDown") dy = step;
    else return;
    e.preventDefault();
    const [kx, ky] = OFFSET_KEYS[activeHandle] || OFFSET_KEYS.text;
    const clamp = (v) => Math.max(-500, Math.min(500, v));
    update({ [kx]: clamp((overlay[kx] || 0) + dx), [ky]: clamp((overlay[ky] || 0) + dy) });
  };

  // Approximate on-preview position (% of canvas) for the drag handles. The
  // drag delta is exact regardless, so movement always tracks 1:1.
  const handlePos = (target) => {
    const W = cw, H = ch;
    const pct = (x, y) => ({ left: `${(x / W) * 100}%`, top: `${(y / H) * 100}%` });
    const m = Math.round(W * 0.055) + 60;
    const cornerXY = (pos, hw, hh) => {
      const x = pos.endsWith("l") ? m + hw : pos.endsWith("r") ? W - m - hw : W / 2;
      const y = pos.startsWith("t") ? m + hh : pos.startsWith("b") ? H - m - hh : H / 2;
      return [x, y];
    };
    if (target === "logo") {
      const [x, y] = cornerXY(overlay.logo_position || "tr", W * 0.09, H * 0.025);
      return pct(x + (overlay.logo_offset_x || 0), y + (overlay.logo_offset_y || 0));
    }
    if (target === "clientlogo") {
      const [x, y] = cornerXY(overlay.client_logo_position || "bl", W * 0.08, H * 0.025);
      return pct(x + (overlay.client_logo_offset_x || 0), y + (overlay.client_logo_offset_y || 0));
    }
    if (target === "covermeta") {
      const [x, y] = cornerXY(overlay.meta_anchor || "br", W * 0.1, H * 0.03);
      return pct(x + (overlay.meta_offset_x || 0), y + (overlay.meta_offset_y || 0));
    }
    if (target === "subhead") {
      const a = overlay.anchor || "tl";
      const x = a.endsWith("l") ? m : a.endsWith("r") ? W - m : W / 2;
      const y = a.startsWith("t") ? H * 0.32 : a.startsWith("b") ? H - m : H * 0.55;
      return pct(x + (overlay.subhead_offset_x || 0), y + (overlay.subhead_offset_y || 0));
    }
    if (target === "text") {
      const [x, y] = cornerXY(overlay.anchor || "tl", W * 0.12, H * 0.04);
      return pct(x + (overlay.offset_x || 0), y + (overlay.offset_y || 0));
    }
    // pill
    const sc = (overlay.contact_pill_size || 100) / 100;
    const [x, y] = cornerXY(overlay.contact_pill_position || "bl", W * 0.12 * sc, H * 0.03 * sc);
    return pct(x + (overlay.contact_pill_offset_x || 0), y + (overlay.contact_pill_offset_y || 0));
  };

  const composeNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    compose(overlay);
  };

  // ── Undo / Redo: restore a previously-rendered overlay spec ──
  const applyHistorySpec = (spec) => {
    setOverlay(spec);
    lastSentRef.current = JSON.stringify(spec);
    dirtyRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    compose(spec, true);   // fromHistory=true → don't push a new checkpoint
  };
  const undo = () => {
    const h = historyRef.current;
    if (h.idx <= 0) return;
    h.idx -= 1;
    refreshUndoFlags();
    applyHistorySpec(JSON.parse(h.stack[h.idx]));
  };
  const redo = () => {
    const h = historyRef.current;
    if (h.idx >= h.stack.length - 1) return;
    h.idx += 1;
    refreshUndoFlags();
    applyHistorySpec(JSON.parse(h.stack[h.idx]));
  };

  // ── Photo transforms (flip / rotate) ──
  const toggleFlipH = () => update({ flip_horizontal: !overlay.flip_horizontal });
  const toggleFlipV = () => update({ flip_vertical: !overlay.flip_vertical });
  const rotate90 = () => update({ rotate: (((overlay.rotate || 0) + 90) % 360) });

  // ── Arrow nudge (10px steps), clamped to the compositor's ±500 range ──
  const nudge = (dx, dy) => {
    const clamp = (v) => Math.max(-500, Math.min(500, v));
    update({
      offset_x: clamp((overlay.offset_x || 0) + dx),
      offset_y: clamp((overlay.offset_y || 0) + dy),
    });
  };

  // One-click "Make brand-ready": pulls the card's writing into the image
  // (title -> headline, CTA -> subhead), applies the RESILIGENT brand-blue
  // wash, turns on auto logo-contrast, and uses the brand fonts — then renders
  // immediately. Everything stays editable afterward so the user can amend +
  // download.
  const makeBrandReady = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const brandReady = {
      ...overlay,
      headline: (card?.title || overlay.headline || "").slice(0, 100),
      subhead: (card?.cta || overlay.subhead || "").slice(0, 120),
      overlay_enabled: true,
      overlay_style: "full",
      overlay_color: RESILIGENT_BRAND_BLUE,
      overlay_opacity: 0.3,
      duotone_enabled: true,
      duotone_strength: 1.0,
      show_logo: true,
      logo_auto_contrast: true,
      heading_font: "brand:heading",
      accent_font: "brand:accent",
    };
    setOverlay(brandReady);
    lastSentRef.current = JSON.stringify(brandReady);
    compose(brandReady);
  };

  const uploadFont = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/font`,
        { method: "POST", credentials: "include", body: fd }
      );
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b?.detail || "Font upload failed");
      toast.success("Font uploaded");
      // Refresh fonts list and use the new font for heading
      setFonts(prev => [...prev.filter(f => f.kind !== "custom"), { key: b.key, label: b.label, kind: "custom" }]);
      update({ heading_font: b.key, custom_font_path: b.key.replace(/^custom:/, "") });
    } catch (e) {
      toast.error(e.message || "Font upload failed");
    }
  };

  const uploadClientLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image/client-logo`,
        { method: "POST", credentials: "include", body: fd }
      );
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b?.detail || "Client logo upload failed");
      toast.success("Client logo added");
      // Server recomposed already; reflect path + defaults locally.
      setOverlay((prev) => ({ ...prev, ...(b.overlay || {}), client_logo_path: b.client_logo_path }));
      setImgVersion((v) => v + 1);
      onComposed && onComposed(b);
    } catch (e) {
      toast.error(e.message || "Client logo upload failed");
    }
  };

  const removeClientLogo = () => update({ client_logo_path: null });

  // Shadow presets — apply to text AND both logos at once (None / Soft / Strong).
  const applyShadowPreset = (preset) => {
    if (preset === "none") {
      update({ text_shadow: false, logo_shadow: false, client_logo_shadow: false });
    } else if (preset === "soft") {
      update({
        text_shadow: true, text_shadow_opacity: 0.4, text_shadow_blur: 4, text_shadow_distance: 3, text_shadow_direction: 135,
        logo_shadow: true, client_logo_shadow: true,
        logo_shadow_opacity: 0.35, logo_shadow_blur: 14, logo_shadow_distance: 6, logo_shadow_direction: 135,
      });
    } else if (preset === "strong") {
      update({
        text_shadow: true, text_shadow_opacity: 0.6, text_shadow_blur: 8, text_shadow_distance: 6, text_shadow_direction: 135,
        logo_shadow: true, client_logo_shadow: true,
        logo_shadow_opacity: 0.55, logo_shadow_blur: 26, logo_shadow_distance: 12, logo_shadow_direction: 135,
      });
    }
  };

  // Cover layout presets. Back cover = minimalist: clear sub-text, lighten the
  // wash so it stays clean/colour-matched, keep logos + a soft shadow.
  const applyCoverLayout = (kind) => {
    if (kind === "back") {
      update({
        subhead: "", overlay_enabled: true, overlay_style: "full", overlay_opacity: 0.12,
        show_contact_pill: false, show_logo: true,
        logo_shadow: true, client_logo_shadow: true,
        logo_shadow_opacity: 0.35, logo_shadow_blur: 14, logo_shadow_distance: 6,
      });
      toast.message("Back-cover minimal applied — add only a short tagline if needed.");
    } else {
      update({ overlay_enabled: true, overlay_style: "full", overlay_opacity: 0.35, show_contact_pill: true });
    }
  };

  const composedUrl = card?.image_url ? `${card.image_url}${card.image_url.includes("?") ? "&" : "?"}v=${imgVersion}` : null;
  const plateUrl = card?.image_plate_path
    ? `${API}/v2/storage/serve?path=${encodeURIComponent(card.image_plate_path)}`
    : null;
  const previewUrl = previewMode === "plate" && plateUrl ? plateUrl : composedUrl;
  const canTogglePlate = !!plateUrl;
  const _fmtSizes = FORMAT_SIZE_DEFAULTS[overlay.format || "square_1080"] || FORMAT_SIZE_DEFAULTS.square_1080;
  // Preview container must use the format's TRUE aspect ratio (not a forced
  // square) so the rendered image is uncropped and drag-delta / handle math
  // maps 1:1 to the compositor canvas. Square stays square; story/carousel/etc.
  // take their real ratio from ARTIFACT_FORMATS.
  const _fmtAspect = ARTIFACT_FORMATS.find((f) => f.key === (overlay.format || "square_1080"))?.ratio || "aspect-square";
  // Live-edit state: while the overlay has unsaved changes we render an instant
  // browser approximation over the raw plate so the editor feels immediate
  // (Canva-style). The authoritative server PNG swaps back in once saved.
  const _dirty = JSON.stringify(overlay) !== lastSentRef.current;
  // Always render the live CSS overlay layer over the plate (when one exists)
  // in the composed view — not just while _dirty — so there is NO visual switch
  // from server-PNG → CSS-overlay when a drag starts (which used to make text
  // visibly jump on grab). The raw-plate toggle still shows the bare plate.
  const liveEditing = !!plateUrl && stageW > 0 && previewMode !== "plate";
  const liveBg = liveEditing ? plateUrl : previewUrl;

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-3" data-testid="image-overlay-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Type className="h-3.5 w-3.5 text-slate-700" />
          <span className="text-xs font-semibold text-slate-800">Image text & styling</span>
        </div>
        <div className="flex items-center gap-2">
          {composing ? (
            <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="overlay-save-state"><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</span>
          ) : _dirty ? (
            <span className="text-[11px] text-amber-600" data-testid="overlay-save-state">Unsaved…</span>
          ) : (
            <span className="text-[11px] text-emerald-600" data-testid="overlay-save-state">Saved</span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={undo}
            disabled={!canUndo || composing}
            className="h-7 px-2 text-[11px]"
            data-testid="overlay-undo-btn"
            title="Undo last change"
          >
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={redo}
            disabled={!canRedo || composing}
            className="h-7 px-2 text-[11px]"
            data-testid="overlay-redo-btn"
            title="Redo"
          >
            <Redo2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            onClick={composeNow}
            disabled={composing}
            className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="overlay-compose-now-btn"
          >
            {composing ? "Rendering…" : "Recompose now"}
          </Button>
        </div>
      </div>

      <button
        type="button"
        onClick={makeBrandReady}
        disabled={composing}
        className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md text-white text-sm font-semibold shadow-sm hover:brightness-110 active:scale-[0.99] transition disabled:opacity-60"
        style={{ background: `linear-gradient(135deg, ${RESILIGENT_BRAND_BLUE}, #00a6da)` }}
        data-testid="overlay-make-brand-ready-btn"
        title="Pull in the card's writing, apply the RESILIGENT brand-blue wash, auto logo-contrast and brand fonts — in one click. You can amend and download after."
      >
        <Sparkles className="h-4 w-4" />
        {composing ? "Making brand-ready…" : "Make brand-ready"}
      </button>
      <p className="text-[10px] text-slate-500 -mt-1 px-0.5">
        One click: adds your headline + CTA, blue brand wash, auto logo-contrast & brand fonts. Amend anything below, then Download PNG.
      </p>

      {Array.isArray(overlay.cover_variants) && overlay.cover_variants.length > 1 && (
        <div className="rounded-md border border-slate-200 bg-white p-2" data-testid="cover-variant-switcher">
          <div className="text-[11px] font-semibold text-slate-700 mb-1.5">Background variation</div>
          <div className="flex flex-wrap gap-2">
            {overlay.cover_variants.map((v) => {
              const active = overlay.cover_side === v.side && overlay.cover_variant_index === v.variant_index;
              const thumb = `${API}/v2/cover-design/runs/${overlay.cover_run_id}/preview/${v.side}/${v.variant_index}?thumb=1`;
              return (
                <button
                  key={`${v.side}-${v.variant_index}`}
                  type="button"
                  disabled={composing}
                  onClick={() => switchVariant(v.side, v.variant_index)}
                  className={`relative rounded border overflow-hidden transition disabled:opacity-60 ${active ? "border-indigo-500 ring-2 ring-indigo-400" : "border-slate-300 hover:border-slate-400"}`}
                  data-testid={`cover-variant-${v.side}-${v.variant_index}`}
                  title={`${v.side} variation ${v.variant_index + 1}`}
                >
                  <img src={thumb} alt={`${v.side} ${v.variant_index + 1}`} className="w-16 h-20 object-cover" loading="lazy" />
                  <span className="block text-[9px] uppercase text-slate-500 text-center py-0.5">{v.side} {v.variant_index + 1}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {readability && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-[10px] bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="readability-bar">
          <div className="flex items-center gap-1 flex-wrap">
            <Shield className="h-3 w-3 text-slate-600" />
            <span className="font-semibold text-slate-700">Readability</span>
            <ReadabilityBadge label="H" v={readability.headline} testid="r-headline" />
            <ReadabilityBadge label="S" v={readability.subhead} testid="r-subhead" />
            <ReadabilityBadge label="Logo" v={readability.logo} testid="r-logo" />
            <ReadabilityBadge label="Pill" v={readability.contact_pill} testid="r-pill" />
          </div>
          <Button
            size="sm"
            onClick={fixReadability}
            disabled={fixing || composing}
            className={`h-6 px-2 text-[10px] ${readability.overall === "strong" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"} text-white`}
            data-testid="overlay-fix-readability-btn"
          >
            {fixing ? <Loader2 className="h-3 w-3 animate-spin mr-1"/> : <Sparkles className="h-3 w-3 mr-1"/>}
            {readability.overall === "strong" ? "Looking good" : "Fix readability"}
          </Button>
        </div>
      )}

      {/* Preview frames + stress tests (Phase C) — frames hidden for covers */}
      {previewUrl && !isCover && (
        <div className="flex items-center justify-between flex-wrap gap-2 text-[10px]">
          <div className="inline-flex rounded-md bg-white border border-slate-200 overflow-hidden">
            {PREVIEW_FRAMES.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => setPreviewFrame(f.key)}
                className={`px-2 py-1 font-semibold transition ${previewFrame === f.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`preview-frame-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md bg-white border border-slate-200 overflow-hidden">
            {["none", "blur", "greyscale"].map(k => (
              <button
                key={k}
                type="button"
                onClick={() => setStressFilter(k)}
                className={`px-2 py-1 font-semibold transition ${stressFilter === k ? "bg-amber-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`stress-filter-${k}`}
                title={k === "blur" ? "Blur test — can you still read at a glance?" : k === "greyscale" ? "Greyscale contrast test" : "No filter"}
              >
                {k === "none" ? "No filter" : k === "blur" ? "Blur test" : "Greyscale"}
              </button>
            ))}
          </div>
        </div>
      )}

      {previewUrl && (
        <div>
          {isCover && (
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[11px] text-slate-600 mr-auto">Full A4 — drag any element to place it</span>
              <button type="button" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} className="px-2 py-0.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="cover-zoom-out">−</button>
              <span className="text-[11px] w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} className="px-2 py-0.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="cover-zoom-in">+</button>
              <button type="button" onClick={() => setZoom(1)} className="px-2 py-0.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="cover-zoom-fit">Fit</button>
              {!cropping ? (
                <button type="button" onClick={startCrop} className="px-2 py-0.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1" data-testid="cover-crop-start"><Crop className="h-3 w-3" /> Crop</button>
              ) : (
                <>
                  <button type="button" onClick={applyCrop} className="px-2 py-0.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid="cover-crop-apply">Apply</button>
                  <button type="button" onClick={() => setCropping(false)} className="px-2 py-0.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="cover-crop-cancel">Cancel</button>
                </>
              )}
              {(overlay.crop_w > 0 || cropping) && (
                <button type="button" onClick={resetCrop} className="px-2 py-0.5 text-xs rounded border border-rose-300 text-rose-600 bg-white hover:bg-rose-50" data-testid="cover-crop-reset">Reset crop</button>
              )}
            </div>
          )}
          <div className={isCover ? "overflow-auto border border-slate-200 rounded bg-slate-100 max-h-[68vh]" : ((PREVIEW_FRAMES.find(f => f.key === previewFrame)?.wrapClass || "w-full aspect-square").replace("aspect-square", _fmtAspect))}>
            <div className={isCover ? "mx-auto" : "contents"} style={isCover ? { width: `${zoom * 100}%` } : undefined}>
              <div
                ref={previewRef}
                className={`relative outline-none focus:ring-2 focus:ring-indigo-400 rounded ${isCover ? `w-full ${ARTIFACT_FORMATS.find(f => f.key === overlay.format)?.ratio || "aspect-[2480/3508]"}` : "w-full h-full"}`}
                tabIndex={0}
                onKeyDown={onKeyNudge}
                data-testid="overlay-preview-stage"
              >
                <CachedImage
                  src={liveBg}
                  alt={previewMode === "plate" ? "Raw plate" : "Branded post"}
                  className={`w-full h-full object-contain rounded border border-slate-200 bg-white select-none ${STRESS_FILTERS[stressFilter]}`}
                  draggable={false}
                  data-testid="overlay-preview-img"
                />
                {/* ── Instant live preview (while editing) ── */}
                {liveEditing && overlay.duotone_enabled && (
                  <div className="absolute inset-0 pointer-events-none rounded overflow-hidden" data-testid="overlay-live-duotone">
                    <div className="absolute inset-0" style={{ backgroundColor: overlay.duotone_shadow || "#0a2a43", mixBlendMode: "multiply", opacity: Math.min(1, (overlay.duotone_strength ?? 1)) }} />
                    <div className="absolute inset-0" style={{ backgroundColor: overlay.duotone_highlight || "#dff1fb", mixBlendMode: "screen", opacity: Math.min(0.7, (overlay.duotone_strength ?? 1) * 0.5) }} />
                  </div>
                )}
                {liveEditing && overlay.overlay_enabled && (overlay.overlay_style || "left") !== "none" && (
                  <div
                    className="absolute inset-0 pointer-events-none rounded"
                    data-testid="overlay-live-scrim"
                    style={
                      overlay.overlay_style === "full"
                        ? { backgroundColor: overlay.overlay_color || "#0a22e0", opacity: Math.min(0.85, overlay.overlay_opacity ?? 0.55) }
                        : { backgroundImage: `linear-gradient(90deg, ${hexToRgba(overlay.overlay_color || "#0a22e0", Math.min(0.92, overlay.overlay_opacity ?? 0.55))} 0%, ${hexToRgba(overlay.overlay_color || "#0a22e0", 0)} 62%)` }
                    }
                  />
                )}
                {liveEditing && (overlay.subjects || []).map((s, i) => (
                  (s.show === false || !s.path) ? null : (
                    <img
                      key={`live-subj-${i}`}
                      src={`${API}/v2/storage/serve?path=${encodeURIComponent(s.path)}`}
                      alt=""
                      draggable={false}
                      className="absolute pointer-events-none object-contain"
                      style={{
                        zIndex: 5 + i,
                        left: `${((s.x ?? 0.5) - Math.min(0.98, (s.box_w ?? 0.6) * (s.scale ?? 1)) / 2) * 100}%`,
                        top: `${((s.y ?? 0.5) - Math.min(0.98, (s.box_h ?? 0.6) * (s.scale ?? 1)) / 2) * 100}%`,
                        width: `${Math.min(0.98, (s.box_w ?? 0.6) * (s.scale ?? 1)) * 100}%`,
                        height: `${Math.min(0.98, (s.box_h ?? 0.6) * (s.scale ?? 1)) * 100}%`,
                        opacity: s.opacity ?? 1,
                      }}
                    />
                  )
                ))}
                {/* Live client logo (covers) */}
                {liveEditing && overlay.client_logo_path && (() => {
                  const pos = handlePos("clientlogo");
                  const wpct = 16 * ((overlay.client_logo_size || 100) / 100);
                  return (
                    <img
                      src={`${API}/v2/storage/serve?path=${encodeURIComponent(overlay.client_logo_path)}`}
                      alt=""
                      draggable={false}
                      data-testid="overlay-live-clientlogo"
                      className="absolute pointer-events-none object-contain -translate-x-1/2 -translate-y-1/2"
                      style={{ zIndex: 16, left: pos.left, top: pos.top, width: `${wpct}%`, opacity: overlay.client_logo_opacity ?? 1 }}
                    />
                  );
                })()}
                {/* Live headline + (attached) subhead */}
                {liveEditing && (overlay.headline || (overlay.subhead && !overlay.subhead_independent)) && (() => {
                  const scale = stageW / cw;
                  const a = overlay.anchor || "tl";
                  const pos = handlePos("text");
                  const alignH = a.endsWith("r") ? "right" : a.endsWith("l") ? "left" : "center";
                  const tx = a.endsWith("r") ? "-100%" : a.endsWith("l") ? "0%" : "-50%";
                  const ty = a.startsWith("b") ? "-100%" : a.startsWith("t") ? "0%" : "-50%";
                  const headPx = (overlay.heading_size || _fmtSizes.heading) * scale;
                  const accPx = (overlay.accent_size || _fmtSizes.accent) * scale;
                  const tShadow = overlay.text_shadow
                    ? `${(overlay.text_shadow_distance || 4) * scale}px ${(overlay.text_shadow_distance || 4) * scale}px ${(overlay.text_shadow_blur || 4) * scale}px rgba(0,0,0,${overlay.text_shadow_opacity ?? 0.55})`
                    : "none";
                  const tbStyle = overlay.text_box_enabled ? {
                    backgroundColor: hexToRgba(overlay.text_box_color || "#ffffff", overlay.text_box_style === "solid" ? 1 : (overlay.text_box_opacity ?? 0.85)),
                    borderRadius: `${(overlay.text_box_radius || 16) * scale}px`,
                    padding: `${(overlay.text_box_padding || 24) * scale}px`,
                    backdropFilter: overlay.text_box_style === "glass" ? `blur(${(overlay.text_box_blur || 12) * scale}px)` : undefined,
                    boxShadow: overlay.text_box_shadow ? `0 ${6 * scale}px ${18 * scale}px rgba(0,0,0,0.25)` : undefined,
                  } : {};
                  return (
                    <div
                      className="absolute pointer-events-none select-none"
                      data-testid="overlay-live-text"
                      style={{
                        left: pos.left,
                        top: pos.top,
                        transform: `translate(${tx}, ${ty})`,
                        textAlign: alignH,
                        maxWidth: "80%",
                        zIndex: 15,
                        lineHeight: overlay.line_spacing || 1.08,
                        ...tbStyle,
                      }}
                    >
                      {overlay.headline ? (
                        <div style={{
                          fontSize: `${headPx}px`,
                          fontFamily: FONT_STACK_HEADING,
                          fontWeight: overlay.heading_weight === "regular" ? 500 : 800,
                          fontStyle: overlay.heading_italic ? "italic" : "normal",
                          color: overlay.heading_color || "#ffffff",
                          textTransform: CSS_TRANSFORM[overlay.heading_transform] || "none",
                          letterSpacing: `${(overlay.heading_letter_spacing || 0) * scale}px`,
                          textShadow: tShadow,
                          WebkitTextStroke: overlay.text_stroke ? `${(overlay.text_stroke_width || 2) * scale}px ${overlay.text_stroke_color || "#000"}` : undefined,
                        }}>{overlay.headline}</div>
                      ) : null}
                      {(overlay.subhead && !overlay.subhead_independent) ? (
                        <div style={{
                          marginTop: `${8 * scale}px`,
                          fontSize: `${accPx}px`,
                          fontFamily: FONT_STACK_ACCENT,
                          fontWeight: overlay.accent_weight === "bold" ? 700 : 500,
                          fontStyle: overlay.accent_italic ? "italic" : "normal",
                          color: overlay.accent_color || "#ffffff",
                          textTransform: CSS_TRANSFORM[overlay.accent_transform] || "none",
                          letterSpacing: `${(overlay.accent_letter_spacing || 0) * scale}px`,
                          textShadow: tShadow,
                        }}>{overlay.subhead}</div>
                      ) : null}
                    </div>
                  );
                })()}
                {/* Live independent sub-header */}
                {liveEditing && overlay.subhead_independent && (overlay.subhead || "").trim() && (() => {
                  const scale = stageW / cw;
                  const a = overlay.anchor || "tl";
                  const pos = handlePos("subhead");
                  const alignH = a.endsWith("r") ? "right" : a.endsWith("l") ? "left" : "center";
                  const tx = a.endsWith("r") ? "-100%" : a.endsWith("l") ? "0%" : "-50%";
                  const accPx = (overlay.accent_size || _fmtSizes.accent) * scale;
                  return (
                    <div className="absolute pointer-events-none select-none" data-testid="overlay-live-subhead" style={{ left: pos.left, top: pos.top, transform: `translate(${tx}, -50%)`, textAlign: alignH, maxWidth: "80%", zIndex: 15 }}>
                      <div style={{
                        fontSize: `${accPx}px`,
                        fontFamily: FONT_STACK_ACCENT,
                        fontWeight: overlay.accent_weight === "bold" ? 700 : 500,
                        fontStyle: overlay.accent_italic ? "italic" : "normal",
                        color: overlay.accent_color || "#ffffff",
                        textTransform: CSS_TRANSFORM[overlay.accent_transform] || "none",
                        textShadow: overlay.text_shadow ? `0 ${2 * scale}px ${(overlay.text_shadow_blur || 4) * scale}px rgba(0,0,0,${overlay.text_shadow_opacity ?? 0.55})` : "none",
                      }}>{overlay.subhead}</div>
                    </div>
                  );
                })()}
                {/* Live cover meta block (date / prepared-by / note) */}
                {liveEditing && overlay.show_cover_meta && [overlay.meta_date, overlay.meta_prepared_by, overlay.meta_custom].some(t => (t || "").trim()) && (() => {
                  const scale = stageW / cw;
                  const a = overlay.meta_anchor || "br";
                  const pos = handlePos("covermeta");
                  const alignH = a.endsWith("r") ? "right" : a.endsWith("l") ? "left" : "center";
                  const tx = a.endsWith("r") ? "-100%" : a.endsWith("l") ? "0%" : "-50%";
                  const ty = a.startsWith("b") ? "-100%" : a.startsWith("t") ? "0%" : "-50%";
                  const mPx = ((overlay.meta_size && overlay.meta_size > 0) ? overlay.meta_size : Math.max(18, cw / 110)) * scale;
                  return (
                    <div className="absolute pointer-events-none select-none" data-testid="overlay-live-meta" style={{ left: pos.left, top: pos.top, transform: `translate(${tx}, ${ty})`, textAlign: alignH, zIndex: 16 }}>
                      {[overlay.meta_date, overlay.meta_prepared_by, overlay.meta_custom].filter(t => (t || "").trim()).map((t, i) => (
                        <div key={i} style={{ fontSize: `${mPx}px`, fontFamily: FONT_STACK_ACCENT, color: "#ffffff", lineHeight: 1.4, textShadow: `0 ${1.5 * scale}px ${3 * scale}px rgba(0,0,0,0.5)` }}>{t}</div>
                      ))}
                    </div>
                  );
                })()}
                {/* Live tagline (only when an explicit tagline override is set) */}
                {liveEditing && overlay.show_tagline !== false && (overlay.tagline_text || "").trim() && (() => {
                  const scale = stageW / cw;
                  const pos = handlePos("pill");
                  return (
                    <div className="absolute pointer-events-none select-none -translate-y-1/2" data-testid="overlay-live-tagline" style={{ left: pos.left, top: pos.top, zIndex: 16 }}>
                      <div style={{ fontSize: `${(overlay.tagline_size || 22) * scale}px`, fontFamily: FONT_STACK_ACCENT, fontWeight: 600, letterSpacing: `${1 * scale}px`, color: overlay.tagline_color || "#dff1fb", textTransform: "uppercase" }}>{overlay.tagline_text}</div>
                    </div>
                  );
                })()}
                {/* Text drag — full-stage grip for social; positioned 'Title' handle for covers so the other handles stay clickable */}
                {previewMode === "composed" && !isCover && (
                  <div
                    className="absolute inset-0 cursor-grab active:cursor-grabbing"
                    onMouseDown={(e) => onDragStart(e, "text")}
                    onTouchStart={(e) => onDragStart(e, "text")}
                    data-testid="overlay-drag-grip"
                    title="Drag to move the text — or click and use arrow keys"
                  />
                )}
                {previewMode === "composed" && isCover && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "text" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("text")}
                    onMouseDown={(e) => onDragStart(e, "text")}
                    onTouchStart={(e) => onDragStart(e, "text")}
                    data-testid="overlay-text-handle"
                    title="Drag the headline — or select and use arrow keys"
                  >
                    <Move className="h-3 w-3" /> Title
                  </div>
                )}
                {/* Sub-header — independent drag handle (shown once unlinked) */}
                {previewMode === "composed" && overlay.subhead_independent && (overlay.subhead || "").trim() && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "subhead" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("subhead")}
                    onMouseDown={(e) => onDragStart(e, "subhead")}
                    onTouchStart={(e) => onDragStart(e, "subhead")}
                    data-testid="overlay-subhead-handle"
                    title="Drag the sub-header independently"
                  >
                    <Move className="h-3 w-3" /> Sub
                  </div>
                )}
                {/* Movable subject layer boxes (Compose Studio hand-off, multi-object) */}
                {previewMode === "composed" && (overlay.subjects || []).map((s, i) => (
                  s.show === false ? null : (
                    <div
                      key={i}
                      data-testid={`overlay-subject-box-${i}`}
                      onMouseDown={(e) => onSubjectDragStart(e, i)}
                      onTouchStart={(e) => onSubjectDragStart(e, i)}
                      className={`absolute cursor-move rounded border-2 border-dashed ${activeHandle === "subject" && activeSubjectIdx === i ? "border-indigo-500 bg-indigo-400/10" : "border-indigo-400/70 bg-indigo-400/5"}`}
                      style={{
                        zIndex: 10 + i,
                        left: `${((s.x ?? 0.5) - Math.min(0.98, (s.box_w ?? 0.6) * (s.scale ?? 1)) / 2) * 100}%`,
                        top: `${((s.y ?? 0.5) - Math.min(0.98, (s.box_h ?? 0.6) * (s.scale ?? 1)) / 2) * 100}%`,
                        width: `${Math.min(0.98, (s.box_w ?? 0.6) * (s.scale ?? 1)) * 100}%`,
                        height: `${Math.min(0.98, (s.box_h ?? 0.6) * (s.scale ?? 1)) * 100}%`,
                      }}
                      title="Drag the subject — drag the corner to resize"
                    >
                      <span className="absolute -top-5 left-0 text-[10px] font-medium text-indigo-700 bg-white/80 px-1 rounded">
                        {(overlay.subjects || []).length > 1 ? `Subject ${i + 1}` : "Subject"}
                      </span>
                      {activeSubjectIdx === i && (
                        <div
                          data-testid={`overlay-subject-resize-${i}`}
                          onMouseDown={(e) => onSubjectResizeStart(e, i)}
                          onTouchStart={(e) => onSubjectResizeStart(e, i)}
                          className="absolute -bottom-2 -right-2 h-4 w-4 rounded-full bg-indigo-600 border-2 border-white cursor-nwse-resize"
                        />
                      )}
                    </div>
                  )
                ))}
                {/* Logo handle */}
                {previewMode === "composed" && overlay.show_logo !== false && (overlay.logo_variant || "primary") !== "none" && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "logo" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("logo")}
                    onMouseDown={(e) => onDragStart(e, "logo")}
                    onTouchStart={(e) => onDragStart(e, "logo")}
                    data-testid="overlay-logo-handle"
                    title="Drag the logo — or select and use arrow keys"
                  >
                    <Move className="h-3 w-3" /> Logo
                  </div>
                )}
                {/* Client logo handle (covers) */}
                {previewMode === "composed" && isCover && overlay.client_logo_path && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "clientlogo" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("clientlogo")}
                    onMouseDown={(e) => onDragStart(e, "clientlogo")}
                    onTouchStart={(e) => onDragStart(e, "clientlogo")}
                    data-testid="overlay-clientlogo-handle"
                    title="Drag the client logo"
                  >
                    <Move className="h-3 w-3" /> Client
                  </div>
                )}
                {/* Cover meta handle (covers) */}
                {previewMode === "composed" && isCover && overlay.show_cover_meta && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "covermeta" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("covermeta")}
                    onMouseDown={(e) => onDragStart(e, "covermeta")}
                    onTouchStart={(e) => onDragStart(e, "covermeta")}
                    data-testid="overlay-covermeta-handle"
                    title="Drag the date / prepared-by block"
                  >
                    <Move className="h-3 w-3" /> Info
                  </div>
                )}
                {/* Contact pill handle (social only — hidden on covers) */}
                {previewMode === "composed" && !isCover && overlay.show_contact_pill !== false && (
                  <div
                    className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold cursor-grab active:cursor-grabbing select-none shadow-md border transition ${activeHandle === "pill" ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-300" : "bg-white/90 text-slate-700 border-slate-300 hover:bg-white"}`}
                    style={handlePos("pill")}
                    onMouseDown={(e) => onDragStart(e, "pill")}
                    onTouchStart={(e) => onDragStart(e, "pill")}
                    data-testid="overlay-pill-handle"
                    title="Drag the contact pill — or select and use arrow keys"
                  >
                    <Move className="h-3 w-3" /> Pill
                  </div>
                )}
                {/* Crop overlay (covers) */}
                {isCover && cropping && (
                  <div
                    className="absolute inset-0 z-30"
                    onMouseMove={onCropMove}
                    onMouseUp={onCropEnd}
                    onMouseLeave={onCropEnd}
                    onTouchMove={onCropMove}
                    onTouchEnd={onCropEnd}
                    data-testid="cover-crop-overlay"
                  >
                    <div
                      className="absolute border-2 border-emerald-400 bg-emerald-400/10 cursor-move"
                      style={{
                        left: `${cropRect.x * 100}%`, top: `${cropRect.y * 100}%`,
                        width: `${cropRect.w * 100}%`, height: `${cropRect.h * 100}%`,
                        boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                      }}
                      onMouseDown={(e) => onCropDown(e, "move")}
                      onTouchStart={(e) => onCropDown(e, "move")}
                      data-testid="cover-crop-box"
                    >
                      <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-emerald-500 border border-white rounded-sm cursor-nwse-resize" onMouseDown={(e) => onCropDown(e, "nw")} onTouchStart={(e) => onCropDown(e, "nw")} data-testid="cover-crop-nw" />
                      <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-emerald-500 border border-white rounded-sm cursor-nwse-resize" onMouseDown={(e) => onCropDown(e, "se")} onTouchStart={(e) => onCropDown(e, "se")} data-testid="cover-crop-se" />
                    </div>
                  </div>
                )}
                {/* Non-blocking save status — never freezes the canvas */}
                {(composing || liveEditing) && (
                  <div className="absolute bottom-2 right-2 z-40 pointer-events-none" data-testid="overlay-composing">
                    <div className="flex items-center gap-1.5 bg-slate-900/85 text-white text-[11px] px-2.5 py-1 rounded-full shadow-lg">
                      {composing ? (
                        <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
                      ) : (
                        <><span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" /> Live preview</>
                      )}
                    </div>
                  </div>
                )}
                {canTogglePlate && (
                  <div className="absolute top-2 left-2 inline-flex rounded-md shadow-sm bg-white/95 backdrop-blur border border-slate-200 overflow-hidden text-[10px] font-semibold pointer-events-auto">
                    <button
                      type="button"
                      onClick={() => setPreviewMode("composed")}
                      className={`px-2.5 py-1 transition ${previewMode === "composed" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                      data-testid="overlay-preview-mode-composed"
                    >
                      <Layers className="inline h-3 w-3 mr-0.5"/> Composed
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewMode("plate")}
                      className={`px-2.5 py-1 transition ${previewMode === "plate" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                      data-testid="overlay-preview-mode-plate"
                    >
                      <ImgIcon className="inline h-3 w-3 mr-0.5"/> Plate
                    </button>
                  </div>
                )}
                {drag && (
                  <div className="absolute bottom-2 right-2 bg-slate-900/90 text-white text-[10px] font-semibold rounded px-2 py-1 pointer-events-none" data-testid="overlay-drag-readout">
                    {HANDLE_LABELS[drag.target] || "Text"}: {overlay[OFFSET_KEYS[drag.target || "text"][0]] || 0}, {overlay[OFFSET_KEYS[drag.target || "text"][1]] || 0}
                  </div>
                )}
                {!drag && previewMode === "composed" && (
                  <div className="absolute bottom-2 left-2 bg-slate-900/70 text-white text-[9px] rounded px-2 py-0.5 pointer-events-none" data-testid="overlay-active-hint">
                    Editing: <span className="font-semibold">{HANDLE_LABELS[activeHandle] || "Text"}</span> · arrow keys to nudge
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Headline */}
      <div className="grid grid-cols-1 gap-1">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-slate-600">Headline (press Enter to break the line where you want)</Label>
        </div>
        <textarea
          value={overlay.headline || ""}
          onChange={(e) => update({ headline: e.target.value })}
          placeholder={"Strategic\nDrift"}
          rows={2}
          maxLength={120}
          className="w-full text-sm rounded border border-slate-300 bg-white px-2 py-1.5 resize-y leading-snug"
          data-testid="overlay-headline-input"
        />
      </div>

      {/* Subhead */}
      <div className="grid grid-cols-1 gap-1">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-slate-600">Subhead (optional, smaller — Enter adds a line break)</Label>
        </div>
        <textarea
          value={overlay.subhead || ""}
          onChange={(e) => update({ subhead: e.target.value })}
          placeholder="Re-anchor when the macro shifts."
          rows={2}
          maxLength={160}
          className="w-full text-sm rounded border border-slate-300 bg-white px-2 py-1.5 resize-y leading-snug"
          data-testid="overlay-subhead-input"
        />
        <label className="flex items-center gap-1.5 text-[10px] text-slate-600 px-0.5 pt-0.5" data-testid="overlay-subhead-independent-toggle">
          <input
            type="checkbox"
            checked={!!overlay.subhead_independent}
            onChange={(e) => update({ subhead_independent: e.target.checked })}
          />
          Move sub-header independently of the title
        </label>
        {overlay.subhead_independent && (
          <p className="text-[10px] text-indigo-600 px-0.5">
            Drag the <strong>Sub</strong> handle on the page to position the sub-header. Untick to re-attach it under the title.
          </p>
        )}
      </div>

      {isCover && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 space-y-2" data-testid="cover-meta-controls">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-semibold text-slate-700">Cover info block</Label>
            <label className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <input
                type="checkbox"
                checked={overlay.show_cover_meta !== false}
                onChange={(e) => updateCompose({ show_cover_meta: e.target.checked })}
                data-testid="cover-meta-toggle"
              />
              Show on cover
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Date</Label>
              <input
                type="text"
                value={overlay.meta_date || ""}
                onChange={(e) => update({ meta_date: e.target.value })}
                onBlur={composeNow}
                placeholder="June 2026"
                className="w-full text-xs rounded border border-slate-300 bg-white px-2 py-1"
                data-testid="cover-meta-date-input"
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Prepared by</Label>
              <input
                type="text"
                value={overlay.meta_prepared_by || ""}
                onChange={(e) => update({ meta_prepared_by: e.target.value })}
                onBlur={composeNow}
                placeholder="Prepared by RESILIGENT"
                className="w-full text-xs rounded border border-slate-300 bg-white px-2 py-1"
                data-testid="cover-meta-preparedby-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Anything else (optional)</Label>
            <input
              type="text"
              value={overlay.meta_custom || ""}
              onChange={(e) => update({ meta_custom: e.target.value })}
              onBlur={composeNow}
              placeholder="e.g. Confidential · Ref 2026-014"
              className="w-full text-xs rounded border border-slate-300 bg-white px-2 py-1"
              data-testid="cover-meta-custom-input"
            />
          </div>
          <p className="text-[10px] text-slate-500">Drag the <strong>Info</strong> handle on the page to position this block.</p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Heading font</Label>
          <select
            value={overlay.heading_font || "brand:heading"}
            onChange={(e) => update({ heading_font: e.target.value })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
            data-testid="overlay-heading-font-select"
          >
            {fonts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Heading size</Label>
          <input
            type="number"
            min={Math.round(_fmtSizes.heading * 0.25)}
            max={Math.round(_fmtSizes.heading * 2.2)}
            step={2}
            value={overlay.heading_size || _fmtSizes.heading}
            onChange={(e) => update({ heading_size: parseInt(e.target.value || String(_fmtSizes.heading), 10) })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
            data-testid="overlay-heading-size-input"
          />
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Heading colour</Label>
          <ColorPickerCell
            value={overlay.heading_color || ""}
            onChange={(v) => update({ heading_color: v || null })}
            testid="overlay-heading-color"
          />
        </div>
      </div>
      {/* Heading weight / italic / transform / tracking */}
      <div className="grid grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Weight</Label>
          <select
            value={overlay.heading_weight || "bold"}
            onChange={(e) => update({ heading_weight: e.target.value })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
            data-testid="overlay-heading-weight-select"
          >
            {WEIGHT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <label className="flex flex-col text-[10px] text-slate-500">
          <span>Italic</span>
          <div className="mt-2 flex items-center h-8">
            <input
              type="checkbox"
              checked={!!overlay.heading_italic}
              onChange={(e) => update({ heading_italic: e.target.checked })}
              data-testid="overlay-heading-italic-toggle"
            />
          </div>
        </label>
        <div>
          <Label className="text-[10px] text-slate-500">Case</Label>
          <div className="mt-0.5 inline-flex h-8 rounded border border-slate-300 bg-white overflow-hidden text-[10px] w-full">
            {TRANSFORM_OPTIONS.map(o => (
              <button
                key={o.key}
                type="button"
                onClick={() => update({ heading_transform: o.key })}
                className={`flex-1 transition ${(overlay.heading_transform || "none") === o.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`overlay-heading-case-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <NumField
          label="Tracking"
          value={overlay.heading_letter_spacing || 0}
          onChange={(v) => update({ heading_letter_spacing: Math.max(-10, Math.min(30, v)) })}
          min={-10}
          max={30}
          testid="overlay-heading-tracking"
        />
      </div>

      {/* Subhead typography */}
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Subhead font</Label>
          <select
            value={overlay.accent_font || "brand:accent"}
            onChange={(e) => update({ accent_font: e.target.value })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
            data-testid="overlay-accent-font-select"
          >
            {fonts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Subhead size</Label>
          <input
            type="number"
            min={Math.round(_fmtSizes.accent * 0.25)}
            max={Math.round(_fmtSizes.accent * 2.2)}
            step={2}
            value={overlay.accent_size || _fmtSizes.accent}
            onChange={(e) => update({ accent_size: parseInt(e.target.value || String(_fmtSizes.accent), 10) })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
            data-testid="overlay-accent-size-input"
          />
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Subhead colour</Label>
          <ColorPickerCell
            value={overlay.accent_color || ""}
            onChange={(v) => update({ accent_color: v || null })}
            testid="overlay-accent-color"
          />
        </div>
      </div>
      {/* Subhead weight / italic / transform / tracking */}
      <div className="grid grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Weight</Label>
          <select
            value={overlay.accent_weight || "regular"}
            onChange={(e) => update({ accent_weight: e.target.value })}
            className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
            data-testid="overlay-accent-weight-select"
          >
            {WEIGHT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <label className="flex flex-col text-[10px] text-slate-500">
          <span>Italic</span>
          <div className="mt-2 flex items-center h-8">
            <input
              type="checkbox"
              checked={!!overlay.accent_italic}
              onChange={(e) => update({ accent_italic: e.target.checked })}
              data-testid="overlay-accent-italic-toggle"
            />
          </div>
        </label>
        <div>
          <Label className="text-[10px] text-slate-500">Case</Label>
          <div className="mt-0.5 inline-flex h-8 rounded border border-slate-300 bg-white overflow-hidden text-[10px] w-full">
            {TRANSFORM_OPTIONS.map(o => (
              <button
                key={o.key}
                type="button"
                onClick={() => update({ accent_transform: o.key })}
                className={`flex-1 transition ${(overlay.accent_transform || "none") === o.key ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`overlay-accent-case-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <NumField
          label="Tracking"
          value={overlay.accent_letter_spacing || 0}
          onChange={(v) => update({ accent_letter_spacing: Math.max(-10, Math.min(30, v)) })}
          min={-10}
          max={30}
          testid="overlay-accent-tracking"
        />
      </div>

      {/* Hierarchy preset */}
      <div className="grid grid-cols-1 gap-1 pt-1">
        <Label className="text-[10px] text-slate-500">Hierarchy preset — sets heading/subhead size ratio, line-height, block width</Label>
        <select
          value={overlay.preset || "bold"}
          onChange={(e) => update({ preset: e.target.value })}
          className="h-8 text-xs rounded border border-slate-300 bg-white px-2"
          data-testid="overlay-preset-select"
        >
          {PRESET_OPTIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {/* === Subject layers (multi-object, up to 5) === */}
      {(overlay.subjects || []).length > 0 && (() => {
        const subs = overlay.subjects || [];
        const idx = Math.max(0, Math.min(activeSubjectIdx, subs.length - 1));
        const active = subs[idx] || {};
        return (
        <details className="rounded border border-slate-200 bg-white" open data-testid="overlay-subject-section">
          <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
            <span>Subjects ({subs.length})</span>
          </summary>
          <div className="p-2 pt-0 space-y-2">
            <p className="text-[10px] text-slate-500 leading-snug">
              Drag any dashed box on the preview to move a cut-out, or its corner to resize. Select a layer below to edit it.
            </p>
            {subs.length > 1 && (
              <div className="flex flex-wrap gap-1" data-testid="overlay-subject-layer-list">
                {subs.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`overlay-subject-select-${i}`}
                    onClick={() => setActiveSubjectIdx(i)}
                    className={`h-6 px-2 text-[10px] rounded border transition ${idx === i ? "border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"} ${s.show === false ? "opacity-50 line-through" : ""}`}
                  >
                    Subject {i + 1}
                  </button>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-[11px] text-slate-700">
              <input
                type="checkbox"
                checked={active.show !== false}
                onChange={(e) => updateSubject(idx, { show: e.target.checked }, true)}
                data-testid="overlay-subject-show-toggle"
              />
              Show subject {subs.length > 1 ? idx + 1 : ""}
            </label>
            <label className="flex items-center gap-2 text-[11px] text-slate-700">
              <input
                type="checkbox"
                checked={!!active.shadow}
                onChange={(e) => updateSubject(idx, { shadow: e.target.checked }, true)}
                data-testid="overlay-subject-shadow-toggle"
              />
              Drop shadow
            </label>
            {subs.length > 1 && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500">Layer order</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={idx <= 0}
                    onClick={() => moveSubject(idx, -1)}
                    className="h-6 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    data-testid="overlay-subject-send-back"
                  >
                    Send back
                  </button>
                  <button
                    type="button"
                    disabled={idx >= subs.length - 1}
                    onClick={() => moveSubject(idx, 1)}
                    className="h-6 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    data-testid="overlay-subject-bring-forward"
                  >
                    Bring forward
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label className="text-[10px] text-slate-500">Size · {(active.scale ?? 1).toFixed(2)}×</Label>
              <input
                type="range" min={0.4} max={2.5} step={0.05}
                value={active.scale ?? 1.0}
                onChange={(e) => updateSubject(idx, { scale: parseFloat(e.target.value) })}
                className="w-full accent-indigo-600"
                data-testid="overlay-subject-scale-range"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-slate-500">Move subject</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => updateSubject(idx, { x: 0.5, y: 0.5, scale: 1.0 }, true)}
                  className="h-6 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  data-testid="overlay-subject-reset"
                >
                  Reset
                </button>
                {subs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSubject(idx)}
                    className="h-6 px-2 text-[10px] rounded border border-red-300 bg-white text-red-600 hover:bg-red-50"
                    data-testid="overlay-subject-remove"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 w-24">
              <span />
              <button type="button" data-testid="overlay-subject-up" className="rounded border p-1 text-xs hover:bg-slate-50" onClick={() => updateSubject(idx, { y: Math.max(0.02, (active.y ?? 0.5) - 0.03) })}>▲</button>
              <span />
              <button type="button" data-testid="overlay-subject-left" className="rounded border p-1 text-xs hover:bg-slate-50" onClick={() => updateSubject(idx, { x: Math.max(0.02, (active.x ?? 0.5) - 0.03) })}>◀</button>
              <span />
              <button type="button" data-testid="overlay-subject-right" className="rounded border p-1 text-xs hover:bg-slate-50" onClick={() => updateSubject(idx, { x: Math.min(0.98, (active.x ?? 0.5) + 0.03) })}>▶</button>
              <span />
              <button type="button" data-testid="overlay-subject-down" className="rounded border p-1 text-xs hover:bg-slate-50" onClick={() => updateSubject(idx, { y: Math.min(0.98, (active.y ?? 0.5) + 0.03) })}>▼</button>
              <span />
            </div>
          </div>
        </details>
        );
      })()}

      <details className="rounded border border-slate-200 bg-white" open data-testid="overlay-phototransform-section">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
          <span>Photo transform{overlay.flip_horizontal ? " · mirrored" : ""}{overlay.flip_vertical ? " · flipped" : ""}{overlay.rotate ? ` · ${overlay.rotate}°` : ""}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <p className="text-[10px] text-slate-500 leading-snug">
            Flips/rotates the <em>photo only</em> — your text and logo stay the right way round.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={toggleFlipH}
              className={`inline-flex items-center justify-center gap-1.5 h-8 rounded border text-[11px] font-semibold transition ${overlay.flip_horizontal ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid="overlay-flip-h-btn"
              title="Mirror the photo left-to-right (reverse)"
            >
              <FlipHorizontal2 className="h-3.5 w-3.5" /> Mirror
            </button>
            <button
              type="button"
              onClick={toggleFlipV}
              className={`inline-flex items-center justify-center gap-1.5 h-8 rounded border text-[11px] font-semibold transition ${overlay.flip_vertical ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid="overlay-flip-v-btn"
              title="Flip the photo top-to-bottom"
            >
              <FlipVertical2 className="h-3.5 w-3.5" /> Flip
            </button>
            <button
              type="button"
              onClick={rotate90}
              className={`inline-flex items-center justify-center gap-1.5 h-8 rounded border text-[11px] font-semibold transition ${overlay.rotate ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid="overlay-rotate-btn"
              title="Rotate the photo 90° clockwise"
            >
              <RotateCw className="h-3.5 w-3.5" /> {overlay.rotate ? `${overlay.rotate}°` : "Rotate"}
            </button>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">
              Line spacing {overlay.line_spacing == null ? "· auto" : `· ${overlay.line_spacing}px`}
            </Label>
            <div className="mt-0.5 flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={60}
                value={overlay.line_spacing == null ? 8 : overlay.line_spacing}
                onChange={(e) => update({ line_spacing: parseInt(e.target.value || "0", 10) })}
                className="flex-1 accent-indigo-600"
                data-testid="overlay-line-spacing-range"
              />
              <button
                type="button"
                onClick={() => update({ line_spacing: null })}
                className="h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                data-testid="overlay-line-spacing-reset"
                title="Back to automatic spacing"
              >
                Auto
              </button>
            </div>
          </div>
        </div>
      </details>

      {/* === Background overlay layer === */}
      <details className="rounded border border-slate-200 bg-white" open>
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
          <span>Background overlay {overlay.overlay_enabled ? `· ${overlay.overlay_style} · ${Math.round((overlay.overlay_opacity || 0) * 100)}%` : "· off"}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <button
            type="button"
            onClick={() => update({
              overlay_enabled: true,
              overlay_style: "full",
              overlay_color: RESILIGENT_BRAND_BLUE,
              overlay_opacity: 0.45,
            })}
            className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-2.5 text-[11px] font-semibold rounded text-white hover:brightness-110 transition"
            style={{ backgroundColor: RESILIGENT_BRAND_BLUE }}
            data-testid="overlay-brand-wash-preset"
            title="Apply a strong RESILIGENT brand-blue wash over the photo"
          >
            Apply RESILIGENT brand-blue wash
          </button>

          <div className="rounded border border-sky-200 bg-sky-50/60 p-2 space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-800">
              <input
                type="checkbox"
                checked={!!overlay.duotone_enabled}
                onChange={(e) => update({ duotone_enabled: e.target.checked })}
                data-testid="overlay-duotone-toggle"
              /> Brand duotone (recolour photo to your blue)
            </label>
            <p className="text-[10px] text-slate-500 leading-snug">
              Forces any photo into the RESILIGENT blue palette so it always matches your brand.
            </p>
            {overlay.duotone_enabled && (
              <div data-testid="overlay-duotone-strength-wrap">
                <Label className="text-[10px] text-slate-500">
                  Strength ({Math.round((overlay.duotone_strength ?? 1) * 100)}%)
                </Label>
                <input
                  type="range"
                  min="0.3"
                  max="1"
                  step="0.05"
                  value={overlay.duotone_strength ?? 1}
                  onChange={(e) => update({ duotone_strength: parseFloat(e.target.value) })}
                  className="w-full accent-sky-600"
                  data-testid="overlay-duotone-strength"
                />
                <p className="text-[10px] text-slate-400">Lower keeps a hint of the original photo; 100% is fully on-brand.</p>
              </div>
            )}
          </div>

          <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
            <input
              type="checkbox"
              checked={!!overlay.overlay_enabled}
              onChange={(e) => update({ overlay_enabled: e.target.checked })}
              data-testid="overlay-enabled-toggle"
            /> Enable background overlay
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Style</Label>
              <select
                value={overlay.overlay_style || "left"}
                onChange={(e) => update({ overlay_style: e.target.value })}
                className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
                data-testid="overlay-style-select"
              >
                {OVERLAY_STYLE_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Opacity ({Math.round((overlay.overlay_opacity || 0) * 100)}%)</Label>
              <input
                type="range"
                min={0}
                max={80}
                value={Math.round((overlay.overlay_opacity || 0) * 100)}
                onChange={(e) => update({ overlay_opacity: parseInt(e.target.value || "0", 10) / 100 })}
                className="mt-2 w-full"
                data-testid="overlay-opacity-range"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Overlay colour</Label>
            <ColorPickerCell
              value={overlay.overlay_color || "#0a22e0"}
              onChange={(v) => update({ overlay_color: v || "#0a22e0" })}
              testid="overlay-color"
            />
          </div>
        </div>
      </details>

      {/* === Text effects === */}
      <details className="rounded border border-slate-200 bg-white">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none">
          Text legibility effects {overlay.text_shadow ? "· shadow" : ""}{overlay.text_stroke ? " · stroke" : ""}
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1 text-[11px]">
              <input
                type="checkbox"
                checked={!!overlay.text_shadow}
                onChange={(e) => update({ text_shadow: e.target.checked })}
                data-testid="overlay-text-shadow-toggle"
              /> Shadow
            </label>
            <label className="flex items-center gap-1 text-[11px]">
              <input
                type="checkbox"
                checked={!!overlay.text_stroke}
                onChange={(e) => update({ text_stroke: e.target.checked })}
                data-testid="overlay-text-stroke-toggle"
              /> Stroke
            </label>
          </div>
          {overlay.text_shadow && (
            <div className="grid grid-cols-4 gap-2">
              <NumField label="Opacity %" value={Math.round((overlay.text_shadow_opacity || 0) * 100)} onChange={(v) => update({ text_shadow_opacity: v / 100 })} min={0} max={100} testid="overlay-shadow-op"/>
              <NumField label="Blur" value={overlay.text_shadow_blur || 0} onChange={(v) => update({ text_shadow_blur: v })} min={0} max={40} testid="overlay-shadow-blur"/>
              <NumField label="Distance" value={overlay.text_shadow_distance || 0} onChange={(v) => update({ text_shadow_distance: v })} min={0} max={40} testid="overlay-shadow-dist"/>
              <NumField label="Angle°" value={overlay.text_shadow_direction || 0} onChange={(v) => update({ text_shadow_direction: v })} min={0} max={359} testid="overlay-shadow-dir"/>
            </div>
          )}
          {overlay.text_stroke && (
            <div className="grid grid-cols-2 gap-2">
              <NumField label="Width (1–3)" value={overlay.text_stroke_width || 2} onChange={(v) => update({ text_stroke_width: Math.max(1, Math.min(3, v)) })} min={1} max={3} testid="overlay-stroke-w"/>
              <div>
                <Label className="text-[10px] text-slate-500">Stroke colour</Label>
                <ColorPickerCell
                  value={overlay.text_stroke_color || "#000000"}
                  onChange={(v) => update({ text_stroke_color: v || "#000000" })}
                  testid="overlay-stroke-color"
                />
              </div>
            </div>
          )}
        </div>
      </details>

      {/* === Text readability box === */}
      <details className="rounded border border-slate-200 bg-white">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none">
          Text readability box {overlay.text_box_enabled ? `· ${overlay.text_box_style}` : "· off"}
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
            <input
              type="checkbox"
              checked={!!overlay.text_box_enabled}
              onChange={(e) => update({ text_box_enabled: e.target.checked })}
              data-testid="overlay-textbox-enabled-toggle"
            /> Enable readability box behind text
          </label>
          {overlay.text_box_enabled && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Style</Label>
                  <select
                    value={overlay.text_box_style || "semi"}
                    onChange={(e) => update({ text_box_style: e.target.value })}
                    className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
                    data-testid="overlay-textbox-style-select"
                  >
                    {TEXT_BOX_STYLE_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Opacity ({Math.round((overlay.text_box_opacity || 0) * 100)}%)</Label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round((overlay.text_box_opacity || 0) * 100)}
                    onChange={(e) => update({ text_box_opacity: parseInt(e.target.value || "0", 10) / 100 })}
                    className="mt-2 w-full"
                    data-testid="overlay-textbox-opacity-range"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Box colour</Label>
                <ColorPickerCell
                  value={overlay.text_box_color || "#ffffff"}
                  onChange={(v) => update({ text_box_color: v || "#ffffff" })}
                  testid="overlay-textbox-color"
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <NumField label="Radius" value={overlay.text_box_radius || 16} onChange={(v) => update({ text_box_radius: v })} min={0} max={80} testid="overlay-textbox-radius"/>
                <NumField label="Padding" value={overlay.text_box_padding || 24} onChange={(v) => update({ text_box_padding: v })} min={0} max={80} testid="overlay-textbox-pad"/>
                <NumField label="Blur (glass)" value={overlay.text_box_blur || 12} onChange={(v) => update({ text_box_blur: v })} min={0} max={40} testid="overlay-textbox-blur"/>
                <label className="flex items-center gap-1 text-[11px] mt-4">
                  <input
                    type="checkbox"
                    checked={!!overlay.text_box_shadow}
                    onChange={(e) => update({ text_box_shadow: e.target.checked })}
                    data-testid="overlay-textbox-shadow-toggle"
                  /> Shadow
                </label>
              </div>
            </>
          )}
        </div>
      </details>

      {/* === Logo controls === */}
      <details className="rounded border border-slate-200 bg-white" open>
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
          <span>Logo · {LOGO_VARIANT_OPTIONS.find(o => o.key === overlay.logo_variant)?.label || "Primary"}{overlay.logo_auto_contrast ? " · Auto contrast" : ""}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Variant</Label>
              <select
                value={overlay.logo_variant || "primary"}
                onChange={(e) => update({ logo_variant: e.target.value })}
                className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
                data-testid="overlay-logo-variant-select"
              >
                {LOGO_VARIANT_OPTIONS.map((o) => {
                  // Primary always exists (kit.logo_path); "none" is a sentinel.
                  const fixedKeys = ["primary", "none"];
                  const missing = !fixedKeys.includes(o.key)
                    && availableLogoVariants.length > 0
                    && !availableLogoVariants.includes(o.key);
                  return (
                    <option key={o.key} value={o.key} data-testid={`overlay-logo-variant-opt-${o.key}`}>
                      {o.label}{missing ? " — not uploaded" : ""}
                    </option>
                  );
                })}
              </select>
              {(() => {
                const v = overlay.logo_variant || "primary";
                const fixedKeys = ["primary", "none"];
                const missing = !fixedKeys.includes(v)
                  && availableLogoVariants.length > 0
                  && !availableLogoVariants.includes(v);
                if (!missing) return null;
                return (
                  <p className="mt-1 text-[10px] text-amber-600 leading-tight" data-testid="overlay-logo-variant-missing">
                    {v} not uploaded — falls back to primary.{" "}
                    <a
                      href="/amplify"
                      onClick={(e) => {
                        // Best-effort: hint the user to upload, no nav hijack.
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("amplify:navigate", { detail: { tab: "brand" } }));
                        toast.info("Open Amplify → Brand → Logo variants to upload.");
                      }}
                      className="underline text-amber-700 hover:text-amber-800"
                      data-testid="overlay-logo-variant-upload-link"
                    >Upload it →</a>
                  </p>
                );
              })()}
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-700 mt-5">
              <input
                type="checkbox"
                checked={!!overlay.logo_auto_contrast}
                onChange={(e) => update({ logo_auto_contrast: e.target.checked })}
                data-testid="overlay-logo-autocontrast-toggle"
              /> Auto logo contrast
            </label>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Position</Label>
              <select
                value={overlay.logo_position || "tr"}
                onChange={(e) => update({ logo_position: e.target.value })}
                className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
                data-testid="overlay-logo-pos-select"
              >
                <option value="tl">Top-left</option>
                <option value="tr">Top-right</option>
                <option value="bl">Bottom-left</option>
                <option value="br">Bottom-right</option>
              </select>
            </div>
            <NumField label="Size %" value={overlay.logo_size || 100} onChange={(v) => update({ logo_size: v })} min={30} max={200} testid="overlay-logo-size"/>
            <NumField label="Opacity %" value={Math.round((overlay.logo_opacity || 1) * 100)} onChange={(v) => update({ logo_opacity: v / 100 })} min={10} max={100} testid="overlay-logo-opacity"/>
            <NumField label="Margin" value={overlay.logo_margin || 60} onChange={(v) => update({ logo_margin: v })} min={20} max={200} testid="overlay-logo-margin"/>
          </div>
          <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
            <Label className="text-[10px] text-slate-500">Move logo (10px)</Label>
            <div className="flex items-center gap-1" data-testid="overlay-logo-nudge-pad">
              <button type="button" onClick={() => update({ logo_offset_x: (overlay.logo_offset_x || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100" data-testid="overlay-logo-nudge-left" title="Logo left"><ArrowLeft className="h-3.5 w-3.5"/></button>
              <button type="button" onClick={() => update({ logo_offset_y: (overlay.logo_offset_y || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100" data-testid="overlay-logo-nudge-up" title="Logo up"><ArrowUp className="h-3.5 w-3.5"/></button>
              <button type="button" onClick={() => update({ logo_offset_y: (overlay.logo_offset_y || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100" data-testid="overlay-logo-nudge-down" title="Logo down"><ArrowDown className="h-3.5 w-3.5"/></button>
              <button type="button" onClick={() => update({ logo_offset_x: (overlay.logo_offset_x || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100" data-testid="overlay-logo-nudge-right" title="Logo right"><ArrowRight className="h-3.5 w-3.5"/></button>
              <span className="ml-1 text-[10px] text-slate-400 font-mono">{overlay.logo_offset_x || 0}, {overlay.logo_offset_y || 0}</span>
              <button type="button" onClick={() => update({ logo_offset_x: 0, logo_offset_y: 0 })} className="ml-1 h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50" data-testid="overlay-logo-nudge-reset">Reset</button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
              <input type="checkbox" checked={!!overlay.logo_shadow} onChange={(e) => update({ logo_shadow: e.target.checked })} data-testid="overlay-logo-shadow"/> Logo drop shadow
            </label>
            <div className="flex items-center gap-1" data-testid="overlay-shadow-presets">
              <span className="text-[10px] text-slate-500">Shadow:</span>
              {["none", "soft", "strong"].map((p) => (
                <button key={p} type="button" onClick={() => applyShadowPreset(p)}
                  className="h-7 px-2 text-[10px] capitalize rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                  data-testid={`overlay-shadow-preset-${p}`}>{p}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1" data-testid="overlay-cover-layout">
            <span className="text-[10px] text-slate-500">Cover layout:</span>
            <button type="button" onClick={() => applyCoverLayout("front")}
              className="h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              data-testid="overlay-cover-front">Front (full)</button>
            <button type="button" onClick={() => applyCoverLayout("back")}
              className="h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              data-testid="overlay-cover-back">Back (minimal)</button>
          </div>
          {overlay.logo_shadow && (
            <div className="grid grid-cols-4 gap-2">
              <NumField label="Opacity %" value={Math.round((overlay.logo_shadow_opacity || 0) * 100)} onChange={(v) => update({ logo_shadow_opacity: v / 100 })} min={0} max={100} testid="overlay-logo-shadow-op"/>
              <NumField label="Blur" value={overlay.logo_shadow_blur || 0} onChange={(v) => update({ logo_shadow_blur: v })} min={0} max={60} testid="overlay-logo-shadow-blur"/>
              <NumField label="Distance" value={overlay.logo_shadow_distance || 0} onChange={(v) => update({ logo_shadow_distance: v })} min={0} max={60} testid="overlay-logo-shadow-dist"/>
              <NumField label="Angle°" value={overlay.logo_shadow_direction || 0} onChange={(v) => update({ logo_shadow_direction: v })} min={0} max={359} testid="overlay-logo-shadow-dir"/>
            </div>
          )}
          <div>
            <Label className="text-[10px] text-slate-500">Logo text (used only when there is no logo image)</Label>
            <input
              type="text"
              value={overlay.brand_name_text || ""}
              placeholder="RESILIGENT"
              onChange={(e) => update({ brand_name_text: e.target.value || null })}
              className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
              data-testid="overlay-logo-text-input"
            />
          </div>
        </div>
      </details>

      {/* === Client (second) logo === */}
      <details className="rounded border border-slate-200 bg-white" data-testid="overlay-client-logo-section">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
          <span>Client logo · {overlay.client_logo_path ? "added" : "none"}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1 h-8 px-2 text-[11px] rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer" data-testid="overlay-client-logo-upload">
              <Upload className="h-3.5 w-3.5" /> {overlay.client_logo_path ? "Replace logo" : "Upload client logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => uploadClientLogo(e.target.files?.[0])} />
            </label>
            {overlay.client_logo_path && (
              <button type="button" onClick={removeClientLogo} data-testid="overlay-client-logo-remove"
                className="h-8 px-2 text-[11px] rounded border border-rose-200 text-rose-600 bg-white hover:bg-rose-50">Remove</button>
            )}
          </div>
          {overlay.client_logo_path && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-500">Position</Label>
                  <select value={overlay.client_logo_position || "bl"} onChange={(e) => update({ client_logo_position: e.target.value })}
                    className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1" data-testid="overlay-client-logo-pos">
                    <option value="tl">Top-left</option>
                    <option value="tr">Top-right</option>
                    <option value="bl">Bottom-left</option>
                    <option value="br">Bottom-right</option>
                  </select>
                </div>
                <NumField label="Size %" value={overlay.client_logo_size || 100} onChange={(v) => update({ client_logo_size: v })} min={30} max={250} testid="overlay-client-logo-size"/>
                <NumField label="Opacity %" value={Math.round((overlay.client_logo_opacity || 1) * 100)} onChange={(v) => update({ client_logo_opacity: v / 100 })} min={10} max={100} testid="overlay-client-logo-opacity"/>
                <NumField label="Margin" value={overlay.client_logo_margin || 60} onChange={(v) => update({ client_logo_margin: v })} min={20} max={250} testid="overlay-client-logo-margin"/>
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <Label className="text-[10px] text-slate-500">Move (10px)</Label>
                <div className="flex items-center gap-1" data-testid="overlay-client-logo-nudge">
                  <button type="button" onClick={() => update({ client_logo_offset_x: (overlay.client_logo_offset_x || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100"><ArrowLeft className="h-3.5 w-3.5"/></button>
                  <button type="button" onClick={() => update({ client_logo_offset_y: (overlay.client_logo_offset_y || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100"><ArrowUp className="h-3.5 w-3.5"/></button>
                  <button type="button" onClick={() => update({ client_logo_offset_y: (overlay.client_logo_offset_y || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100"><ArrowDown className="h-3.5 w-3.5"/></button>
                  <button type="button" onClick={() => update({ client_logo_offset_x: (overlay.client_logo_offset_x || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100"><ArrowRight className="h-3.5 w-3.5"/></button>
                  <span className="ml-1 text-[10px] text-slate-400 font-mono">{overlay.client_logo_offset_x || 0}, {overlay.client_logo_offset_y || 0}</span>
                  <button type="button" onClick={() => update({ client_logo_offset_x: 0, client_logo_offset_y: 0 })} className="ml-1 h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">Reset</button>
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                <input type="checkbox" checked={!!overlay.client_logo_shadow} onChange={(e) => update({ client_logo_shadow: e.target.checked })} data-testid="overlay-client-logo-shadow"/> Drop shadow
              </label>
            </>
          )}
        </div>
      </details>

      {/* === Tagline (under-logo) controls === */}
      <details className="rounded border border-slate-200 bg-white" data-testid="overlay-tagline-section">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-2">
          <span>Tagline · {overlay.show_tagline === false ? "Hidden" : (overlay.tagline_color ? "Custom colour" : "Auto-follows logo")}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
            <input
              type="checkbox"
              checked={overlay.show_tagline !== false}
              onChange={(e) => update({ show_tagline: e.target.checked })}
              data-testid="overlay-tagline-show-toggle"
            /> Show tagline under logo
          </label>
          <div>
            <Label className="text-[10px] text-slate-500">Tagline text (blank = brand default; shows uppercase)</Label>
            <input
              type="text"
              value={overlay.tagline_text || ""}
              placeholder="Leadership Intelligence"
              onChange={(e) => update({ tagline_text: e.target.value || null })}
              className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
              data-testid="overlay-tagline-text-input"
              disabled={overlay.show_tagline === false}
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Tagline size ({overlay.tagline_size || 22}px)</Label>
            <input
              type="range"
              min={10}
              max={64}
              value={overlay.tagline_size || 22}
              onChange={(e) => update({ tagline_size: parseInt(e.target.value || "22", 10) })}
              className="mt-1 w-full accent-indigo-600"
              data-testid="overlay-tagline-size-range"
              disabled={overlay.show_tagline === false}
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-[10px] text-slate-500">Tagline colour (overrides auto-follow)</Label>
              <div className="mt-0.5 flex items-center gap-1.5">
                <input
                  type="color"
                  value={overlay.tagline_color || "#00a6da"}
                  onChange={(e) => update({ tagline_color: e.target.value })}
                  className="h-8 w-12 rounded border border-slate-300 bg-white p-0.5"
                  data-testid="overlay-tagline-color"
                  disabled={overlay.show_tagline === false}
                />
                <input
                  type="text"
                  value={overlay.tagline_color || ""}
                  placeholder="auto"
                  onChange={(e) => update({ tagline_color: e.target.value || null })}
                  className="flex-1 h-8 text-xs rounded border border-slate-300 bg-white px-2 font-mono"
                  data-testid="overlay-tagline-color-hex"
                  disabled={overlay.show_tagline === false}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => update({ tagline_color: null })}
              className="h-8 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              data-testid="overlay-tagline-color-reset"
              disabled={!overlay.tagline_color || overlay.show_tagline === false}
              title="Clear override → tagline follows logo variant"
            >Reset to auto</button>
          </div>
          <div className="flex flex-wrap gap-1">
            {BRAND_SWATCHES.map((sw) => (
              <button
                key={sw}
                type="button"
                onClick={() => update({ tagline_color: sw })}
                className="h-6 w-6 rounded border border-slate-300"
                style={{ backgroundColor: sw }}
                title={sw}
                data-testid={`overlay-tagline-swatch-${sw.replace("#", "")}`}
                disabled={overlay.show_tagline === false}
              />
            ))}
          </div>
        </div>
      </details>

      {/* === Contact pill (resize + move) === */}
      <details className="rounded border border-slate-200 bg-white" data-testid="overlay-contactpill-section">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1">
          <span>Contact pill · {overlay.show_contact_pill === false ? "Hidden" : `${ANCHOR_LABELS[overlay.contact_pill_position] || "Bottom-left"} · ${overlay.contact_pill_size || 100}%`}</span>
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
            <input
              type="checkbox"
              checked={overlay.show_contact_pill !== false}
              onChange={(e) => update({ show_contact_pill: e.target.checked })}
              data-testid="overlay-contactpill-show-toggle"
            /> Show contact pill
          </label>
          <div>
            <Label className="text-[10px] text-slate-500">Pill text (blank = brand default)</Label>
            <input
              type="text"
              value={overlay.contact_text || ""}
              placeholder="info@resiligent.com"
              onChange={(e) => update({ contact_text: e.target.value || null })}
              className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
              data-testid="overlay-contactpill-text-input"
              disabled={overlay.show_contact_pill === false}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Corner</Label>
              <select
                value={overlay.contact_pill_position || "bl"}
                onChange={(e) => update({ contact_pill_position: e.target.value })}
                className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-1"
                data-testid="overlay-contactpill-pos-select"
                disabled={overlay.show_contact_pill === false}
              >
                <option value="tl">Top-left</option>
                <option value="tr">Top-right</option>
                <option value="bl">Bottom-left</option>
                <option value="br">Bottom-right</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Size ({overlay.contact_pill_size || 100}%)</Label>
              <input
                type="range"
                min={30}
                max={200}
                value={overlay.contact_pill_size || 100}
                onChange={(e) => update({ contact_pill_size: parseInt(e.target.value || "100", 10) })}
                className="mt-2 w-full accent-indigo-600"
                data-testid="overlay-contactpill-size-range"
                disabled={overlay.show_contact_pill === false}
              />
            </div>
          </div>
          <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
            <Label className="text-[10px] text-slate-500">Move pill (10px)</Label>
            <div className="flex items-center gap-1" data-testid="overlay-contactpill-nudge-pad">
              <button type="button" disabled={overlay.show_contact_pill === false} onClick={() => update({ contact_pill_offset_x: (overlay.contact_pill_offset_x || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40" data-testid="overlay-contactpill-nudge-left" title="Pill left"><ArrowLeft className="h-3.5 w-3.5"/></button>
              <button type="button" disabled={overlay.show_contact_pill === false} onClick={() => update({ contact_pill_offset_y: (overlay.contact_pill_offset_y || 0) - 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40" data-testid="overlay-contactpill-nudge-up" title="Pill up"><ArrowUp className="h-3.5 w-3.5"/></button>
              <button type="button" disabled={overlay.show_contact_pill === false} onClick={() => update({ contact_pill_offset_y: (overlay.contact_pill_offset_y || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40" data-testid="overlay-contactpill-nudge-down" title="Pill down"><ArrowDown className="h-3.5 w-3.5"/></button>
              <button type="button" disabled={overlay.show_contact_pill === false} onClick={() => update({ contact_pill_offset_x: (overlay.contact_pill_offset_x || 0) + 10 })} className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40" data-testid="overlay-contactpill-nudge-right" title="Pill right"><ArrowRight className="h-3.5 w-3.5"/></button>
              <span className="ml-1 text-[10px] text-slate-400 font-mono">{overlay.contact_pill_offset_x || 0}, {overlay.contact_pill_offset_y || 0}</span>
              <button type="button" disabled={overlay.show_contact_pill === false} onClick={() => update({ contact_pill_offset_x: 0, contact_pill_offset_y: 0 })} className="ml-1 h-7 px-2 text-[10px] rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40" data-testid="overlay-contactpill-nudge-reset">Reset</button>
            </div>
          </div>
        </div>
      </details>

      {/* === Output format / artifact type === */}
      <details className="rounded border border-slate-200 bg-white" data-testid="overlay-format-section">
        <summary className="px-2 py-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none">
          Format · {ARTIFACT_FORMATS.find((f) => f.key === (overlay.format || "square_1080"))?.label || "Square"}
        </summary>
        <div className="p-2 pt-0 grid grid-cols-2 gap-1.5">
          {ARTIFACT_FORMATS.map((f) => {
            const sel = (overlay.format || "square_1080") === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => update({ format: f.key })}
                className={`text-left rounded border px-2 py-1.5 text-[11px] transition ${
                  sel ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`overlay-format-${f.key}`}
              >
                <div className="font-semibold">{f.label}</div>
              </button>
            );
          })}
        </div>
      </details>

      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <Label className="text-[10px] text-slate-500 flex items-center gap-1"><Move className="h-3 w-3"/>Text anchor (drag on image to fine-tune)</Label>
          <div className="mt-1 grid grid-cols-3 gap-1" data-testid="overlay-anchor-grid">
            {ANCHOR_GRID_ORDER.map(a => {
              const isBest = placement?.best?.includes(a);
              const isWorst = placement?.worst?.includes(a);
              const isSel = overlay.anchor === a;
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => update({ anchor: a, offset_x: 0, offset_y: 0 })}
                  className={`h-7 text-[10px] rounded border relative ${
                    isSel
                      ? "bg-indigo-600 text-white border-indigo-700"
                      : isBest
                        ? "bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100"
                        : isWorst
                          ? "bg-red-50 text-red-800 border-red-300 hover:bg-red-100"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                  }`}
                  title={`${ANCHOR_LABELS[a]}${isBest ? " · quiet zone" : isWorst ? " · busy zone — text may read poorly" : ""}`}
                  data-testid={`overlay-anchor-${a}`}
                >
                  {a.toUpperCase()}
                  {isBest && !isSel && <span className="absolute top-0 right-0.5 text-[8px] text-emerald-600">●</span>}
                  {isWorst && !isSel && <span className="absolute top-0 right-0.5 text-[8px] text-red-500">●</span>}
                </button>
              );
            })}
          </div>
          {placement?.best && (
            <p className="mt-1 text-[9px] text-slate-500">
              <span className="text-emerald-700 font-semibold">●</span> = quiet zone (good for text)&nbsp;&nbsp;
              <span className="text-red-600 font-semibold">●</span> = busy zone
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Label className="text-[10px] text-slate-500 flex items-center gap-1"><Move className="h-3 w-3"/>Move text (10px steps)</Label>
            <div className="mt-1 flex items-center gap-1" data-testid="overlay-nudge-pad">
              <button type="button" onClick={() => nudge(-10, 0)} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100" data-testid="overlay-nudge-left" title="Move left"><ArrowLeft className="h-4 w-4"/></button>
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => nudge(0, -10)} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100" data-testid="overlay-nudge-up" title="Move up"><ArrowUp className="h-4 w-4"/></button>
                <button type="button" onClick={() => nudge(0, 10)} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100" data-testid="overlay-nudge-down" title="Move down"><ArrowDown className="h-4 w-4"/></button>
              </div>
              <button type="button" onClick={() => nudge(10, 0)} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100" data-testid="overlay-nudge-right" title="Move right"><ArrowRight className="h-4 w-4"/></button>
              <span className="ml-2 text-[10px] text-slate-400 font-mono">{overlay.offset_x || 0}, {overlay.offset_y || 0}</span>
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Nudge X (±500)</Label>
            <input
              type="number"
              min={-500}
              max={500}
              value={overlay.offset_x || 0}
              onChange={(e) => update({ offset_x: parseInt(e.target.value || "0", 10) })}
              className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
              data-testid="overlay-offset-x-input"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Nudge Y (±500)</Label>
            <input
              type="number"
              min={-500}
              max={500}
              value={overlay.offset_y || 0}
              onChange={(e) => update({ offset_y: parseInt(e.target.value || "0", 10) })}
              className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
              data-testid="overlay-offset-y-input"
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => update({ offset_x: 0, offset_y: 0 })}
            className="col-span-2 h-7 text-[11px]"
            data-testid="overlay-reset-nudge-btn"
          >
            Reset nudge
          </Button>
        </div>
      </div>

      {/* Toggles + custom font upload */}
      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-[11px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!overlay.show_logo}
              onChange={(e) => update({ show_logo: e.target.checked })}
              data-testid="overlay-show-logo-toggle"
            />
            <ImgIcon className="h-3 w-3" /> Logo
          </label>
          <label className="flex items-center gap-1 text-[11px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!overlay.show_contact_pill}
              onChange={(e) => update({ show_contact_pill: e.target.checked })}
              data-testid="overlay-show-pill-toggle"
            />
            {overlay.show_contact_pill ? <Eye className="h-3 w-3"/> : <EyeOff className="h-3 w-3"/>} Contact pill
          </label>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf"
            onChange={(e) => { uploadFont(e.target.files?.[0]); e.target.value = ""; }}
            className="hidden"
            data-testid="overlay-font-upload-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="h-7 text-[11px]"
            data-testid="overlay-font-upload-btn"
          >
            <Upload className="h-3 w-3 mr-1"/> Upload custom font
          </Button>
        </div>
      </div>

      <p className="text-[10px] text-slate-500 leading-relaxed">
        Auto-renders 3 seconds after your last change. Click <span className="font-semibold">Recompose now</span> to render immediately. The post body above is what publishes to LinkedIn — these settings only drive what appears <em>on the image</em>.
      </p>
    </div>
  );
}

function ReadabilityBadge({ label, v, testid }) {
  if (!v) return null;
  const color = {
    strong:     "bg-emerald-100 text-emerald-800 border-emerald-300",
    acceptable: "bg-sky-100 text-sky-800 border-sky-300",
    weak:       "bg-amber-100 text-amber-800 border-amber-300",
    failing:    "bg-red-100 text-red-800 border-red-300",
  }[v.verdict] || "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-semibold ${color}`}
      title={`${label}: ratio ${v.ratio} (${v.verdict})`}
      data-testid={testid}
    >
      {label} {v.ratio}
    </span>
  );
}

function NumField({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-500">{label}</Label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        className="mt-0.5 w-full h-8 text-xs rounded border border-slate-300 bg-white px-2"
        data-testid={testid}
      />
    </div>
  );
}

function ColorPickerCell({ value, onChange, testid }) {
  return (
    <div className="mt-0.5 flex items-center gap-1">
      <input
        type="color"
        value={value || "#0d7cb4"}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 rounded border border-slate-300 bg-white cursor-pointer"
        data-testid={`${testid}-picker`}
      />
      <div className="flex gap-0.5">
        {BRAND_SWATCHES.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className="h-5 w-5 rounded-full border border-slate-300 hover:scale-110 transition"
            style={{ background: c }}
            title={c}
            data-testid={`${testid}-swatch-${c}`}
          />
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="h-5 px-1 text-[9px] rounded border border-slate-300 bg-white text-slate-500 hover:bg-slate-100"
          title="Reset to brand default"
          data-testid={`${testid}-reset`}
        >
          ×
        </button>
      </div>
    </div>
  );
}
